import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  Snackbar,
  InputAdornment,
  Avatar,
  Link as MuiLink,
  Paper,
  Slide,
  Chip,
  Fab
} from '@mui/material';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc, deleteDoc, orderBy, setDoc, updateDoc, limit, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import IosShareIcon from '@mui/icons-material/IosShare';
import InstagramIcon from '@mui/icons-material/Instagram';
import TopBar from './TopBar';
import FormattedTextDisplay from './FormattedTextDisplay';
import { handleGoogleLogin } from "../services/authService";
import InAppBrowserModal from "./InAppBrowserModal";
import HighlightImageExportModal from './HighlightImageExportModal';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import EditNoteIcon from '@mui/icons-material/EditNote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Global } from '@emotion/react';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SEO from './SEO';

// Re-added shuffleArray function definition
function shuffleArray(array) {
  let currentIndex = array.length, randomIndex;
  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  return array;
}

// JSON-LD 스키마 생성 함수 추가
const generateStructuredData = (book, highlightAuthorProfile, displayedUserHighlights) => {
  if (!book) return null;

  const baseUrl = "https://snaplog.blog";
  
  // Book 스키마
  const bookSchema = {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": book.title || "제목 없음",
    "author": book.authors ? book.authors.map(author => ({
      "@type": "Person",
      "name": author
    })) : undefined,
    "publisher": book.publisher ? {
      "@type": "Organization",
      "name": book.publisher
    } : undefined,
    "isbn": book.isbn ? book.isbn.split(' ')[0] : undefined,
    "image": book.thumbnail || book.userThumbnail || `${baseUrl}/images/no-cover.png`,
    "description": book.contents || `${book.title} 독서기록`,
    "aggregateRating": book.starRating ? {
      "@type": "AggregateRating",
      "ratingValue": book.starRating,
      "bestRating": 5,
      "worstRating": 1,
      "ratingCount": 1
    } : undefined,
    "url": `${baseUrl}/book/${book.id}`,
    "datePublished": book.publishedDate || undefined,
    "bookFormat": "http://schema.org/Hardcover"
  };

  // Person 스키마 (하이라이트 작성자)
  const personSchema = highlightAuthorProfile ? {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": highlightAuthorProfile.displayName || "익명",
    "url": `${baseUrl}/u/${highlightAuthorProfile.uid}`,
    "image": highlightAuthorProfile.photoURL || undefined,
    "description": `${highlightAuthorProfile.displayName || "익명"}님의 독서기록`
  } : null;

  // Article 스키마 (하이라이트들)
  const articleSchemas = displayedUserHighlights.map((highlight, index) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${book.title} - 문장 ${index + 1}`,
    "author": {
      "@type": "Person",
      "name": highlightAuthorProfile?.displayName || "익명",
      "url": highlightAuthorProfile ? `${baseUrl}/u/${highlightAuthorProfile.uid}` : undefined
    },
    "publisher": {
      "@type": "Organization",
      "name": "스냅로그",
      "url": baseUrl
    },
    "datePublished": highlight.createdAt?.toDate ? highlight.createdAt.toDate().toISOString() : new Date(highlight.createdAt).toISOString(),
    "dateModified": highlight.updatedAt?.toDate ? highlight.updatedAt.toDate().toISOString() : undefined,
    "articleBody": highlight.text,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${baseUrl}/book/${book.id}`
    },
    "image": book.thumbnail || book.userThumbnail || `${baseUrl}/images/no-cover.png`,
    "isPartOf": {
      "@type": "Book",
      "name": book.title,
      "isbn": book.isbn ? book.isbn.split(' ')[0] : undefined
    }
  }));

  return {
    book: bookSchema,
    person: personSchema,
    articles: articleSchemas
  };
};

function BookDetail() {
  const { id: idFromParams } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInLibrary, setIsInLibrary] = useState(false);
  const [loadingLibraryStatus, setLoadingLibraryStatus] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [error, setError] = useState(null);
  
  // 하이라이트 상태 추가
  const [otherHighlights, setOtherHighlights] = useState([]);
  const [loadingHighlights, setLoadingHighlights] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showDeleteHighlightDialog, setShowDeleteHighlightDialog] = useState(false);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [selectedHighlightForShare, setSelectedHighlightForShare] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const queryParamUid = queryParams.get('uid');
  const [highlightAuthorUid, setHighlightAuthorUid] = useState(null);
  const [highlightAuthorProfile, setHighlightAuthorProfile] = useState(null);
  const [displayedUserHighlights, setDisplayedUserHighlights] = useState([]);
  const isHighlightOwner = currentUser && highlightAuthorUid === currentUser.uid;

  // 인앱 브라우저 감지 모달 상태 추가
  const [inAppBrowserModalOpen, setInAppBrowserModalOpen] = useState(false);
  
  // 로그인 유도 다이얼로그 상태 추가
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  // 정렬 상태 추가
  const [highlightSortOrder, setHighlightSortOrder] = useState('desc'); // 'desc' | 'asc'

  // 가격 상태 추가
  const [bookPrice, setBookPrice] = useState(null); // { priceSales, priceStandard }
  const [priceError, setPriceError] = useState(null);
  const [priceSheetOpen, setPriceSheetOpen] = useState(false);

  // highlight 쿼리 파라미터 추출 (Home에서 전달받은 하이라이트 ID)
  const highlightParam = new URLSearchParams(location.search).get('highlight');
  const [scrollTargetId, setScrollTargetId] = useState(null);

  // 메모 관련 상태
  const [editingMemo, setEditingMemo] = useState(false);
  const [memoText, setMemoText] = useState(book?.memo || '');
  const [memoInput, setMemoInput] = useState(book?.memo || '');
  const [savingMemo, setSavingMemo] = useState(false);
  useEffect(() => { setMemoText(book?.memo || ''); setMemoInput(book?.memo || ''); }, [book]);

  // 읽기 상태 뱃지 텍스트
  const statusBadge =
    book?.readStatus === 'completed' ? '다 읽었어요' :
    book?.readStatus === 'reading' ? '읽는 중이에요' :
    '읽기 전';

  // 읽기 상태 뱃지 색상(흑백톤)
  const statusBadgeStyle =
    book?.readStatus === 'completed' ? { bgcolor: '#222', color: '#fff' } :
    book?.readStatus === 'reading' ? { bgcolor: '#fff', color: '#111', border: '1px solid #ddd' } :
    { bgcolor: '#f5f5f5', color: '#888' };

  // Helper function to format date safely
  const formatDate = (timestampOrDate) => {
    if (!timestampOrDate) return '';
    // Check if it's a Firestore Timestamp
    if (typeof timestampOrDate.toDate === 'function') {
      return timestampOrDate.toDate().toLocaleDateString();
    }
    // Otherwise, assume it's a JS Date object
    if (timestampOrDate instanceof Date) {
      return timestampOrDate.toLocaleDateString();
    }
    // Fallback for unexpected types
    return ''; 
  };

  // ✅ Firebase Auth 상태 리스너
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("[BookDetail] Auth state changed:", user ? user.uid : null);
      setCurrentUser(user);
      if (!user) {
        console.warn("[BookDetail] User logged out. Redirecting might be needed.");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // --- useEffect: 하이라이트 작성자 UID 결정 (기존 유지, currentUser 의존성) ---
  useEffect(() => {
    if (queryParamUid) {
      setHighlightAuthorUid(queryParamUid);
    } else if (idFromParams && idFromParams.includes('_')) {
      // bookDocId에서 uid, isbn 추출
      const [docUid] = idFromParams.split('_');
      setHighlightAuthorUid(docUid);
    } else if (currentUser) {
      setHighlightAuthorUid(currentUser.uid);
    } else {
      // uid가 없을 때는 null로 설정하고 하이라이트 로딩을 완료 상태로 변경
      setHighlightAuthorUid(null);
      setLoadingHighlights(false);
    }
  }, [queryParamUid, idFromParams, currentUser]);

  // --- useEffect: 작성자 프로필 조회 (기존 유지, currentUser 의존성) ---
  useEffect(() => {
    const fetchAuthorProfile = async () => {
      let targetUid = null;
      if (queryParamUid) {
        targetUid = queryParamUid;
      } else if (idFromParams && idFromParams.includes('_')) {
        const [docUid] = idFromParams.split('_');
        targetUid = docUid;
      }
      if (targetUid && (!currentUser || targetUid !== currentUser.uid)) {
        // 내 계정이 아니면 Firestore에서 프로필 조회
        try {
          const userDocRef = doc(db, 'users', targetUid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            setHighlightAuthorProfile({ uid: userDocSnap.id, ...userDocSnap.data() });
          } else {
            setHighlightAuthorProfile({ displayName: '알 수 없는 사용자' });
          }
        } catch (error) {
          setHighlightAuthorProfile({ displayName: '사용자 정보 로딩 오류' });
        }
      } else if (targetUid && currentUser && targetUid === currentUser.uid) {
        setHighlightAuthorProfile({ uid: currentUser.uid, displayName: currentUser.displayName, photoURL: currentUser.photoURL });
      } else {
        setHighlightAuthorProfile(null);
      }
    };
    fetchAuthorProfile();
  }, [queryParamUid, idFromParams, currentUser]);

  // --- 책 정보 로딩 useEffect (대폭 수정) ---
  useEffect(() => {
    const fetchBookData = async () => {
      // ⭐ URL 파라미터 (Firestore 문서 ID: uid_isbn) 유효성 검사
      if (!idFromParams || !idFromParams.includes('_')) {
        console.error("[BookDetail] Error: Invalid Document ID in URL params:", idFromParams);
        setError("잘못된 책 정보 요청입니다 (ID 형식 오류).");
        setLoading(false);
        setLoadingLibraryStatus(false);
        return;
      }

      console.log(`[BookDetail] Starting book data fetch for Document ID: ${idFromParams}`);
      setLoading(true);
      setBook(null);
      setLoadingLibraryStatus(true);
      setError(null);

      try {
        // 1. Firestore에서 문서 ID로 직접 책 정보 가져오기 (핵심 로직)
        console.log(`[BookDetail] Attempting Firestore fetch with Document ID: ${idFromParams}`);
        const bookRef = doc(db, 'books', idFromParams);
        const docSnap = await getDoc(bookRef);

        if (docSnap.exists()) {
          const fetchedBookData = { id: docSnap.id, ...docSnap.data() };
          setBook(fetchedBookData);
          setIsInLibrary(true); // 데이터 가져오기 성공 = 서재에 있음
          console.log("[BookDetail] Found book in Firestore:", fetchedBookData);
        } else {
          // 문서 ID로 찾지 못한 경우 (예: 잘못된 ID)
          console.error(`[BookDetail] Firestore query failed: Document not found for ID: ${idFromParams}`);
          setError("내 서재에서 해당 책 정보를 찾을 수 없습니다.");
          setBook(null);
          setIsInLibrary(false);
        }

      } catch (error) {
        console.error('[BookDetail] Error fetching book data from Firestore:', error);
        setError(`책 정보 로딩 중 오류 발생: ${error.message}`);
        setBook(null);
        setIsInLibrary(false);
      } finally {
        setLoading(false);
        setLoadingLibraryStatus(false); // Firestore 조회 완료
      }
    };

    fetchBookData();
  // idFromParams (Firestore 문서 ID)가 변경될 때마다 실행
  }, [idFromParams]);

  // fetchHighlights 함수 수정
  const fetchHighlights = useCallback(async () => {
    if (!book || !book.isbn || !idFromParams) {
      setDisplayedUserHighlights([]);
      if (!queryParamUid) setOtherHighlights([]);
      setLoadingHighlights(false);
      return;
    }

    setLoadingHighlights(true);
    // bookDocId에서 uid, isbn 추출
    const [extractedUid, extractedIsbn] = idFromParams.split('_');
    const finalUid = queryParamUid || extractedUid;
    const bookIdentifier = (book.isbn || '').split(' ')[0] || extractedIsbn;

    try {
      // 해당 uid의 하이라이트만 조회 (답글이 아닌 원본 하이라이트만)
      const userHighlightsQuery = query(
        collection(db, 'highlights'),
        where('bookId', '==', bookIdentifier),
        where('uid', '==', finalUid),
        orderBy('createdAt', 'desc')
      );
      const userSnapshot = await getDocs(userHighlightsQuery);
      // parentHighlightId가 없는 것만 필터링 (답글이 아닌 원본만)
      const highlights = userSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(h => !h.parentHighlightId);
      setDisplayedUserHighlights(highlights);

      // 다른 사람 하이라이트: URL에 uid 쿼리 파라미터가 없을 때만 조회 (답글 제외)
      if (!queryParamUid) {
        const allHighlightsQuery = query(
          collection(db, 'highlights'),
          where('bookId', '==', bookIdentifier),
          where('uid', '!=', finalUid),
          orderBy('createdAt', 'desc')
        );
        const allSnapshot = await getDocs(allHighlightsQuery);
        // parentHighlightId가 없는 것만 필터링 (답글이 아닌 원본만)
        const allHighlightsData = allSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(h => !h.parentHighlightId);
        const shuffledHighlights = shuffleArray([...allHighlightsData]);
        setOtherHighlights(shuffledHighlights.slice(0, 10));
      } else {
        setOtherHighlights([]);
      }

    } catch (error) {
      console.error('[BookDetail] Error fetching highlights:', error);
      setDisplayedUserHighlights([]);
      setOtherHighlights([]);
    } finally {
      setLoadingHighlights(false);
    }
  }, [book, idFromParams, queryParamUid]);

  // 하이라이트 로딩 useEffect
  useEffect(() => {
    // book 상태가 유효할 때 fetchHighlights 호출
    if (!loading && book && idFromParams) {
      fetchHighlights();
    } else if (!loading && book) {
      // book은 있지만 idFromParams가 없는 경우 (공개 뷰)
      setLoadingHighlights(false);
    }
  }, [loading, book, idFromParams, fetchHighlights]);

  // 답글 조회 함수
  const fetchReplies = useCallback(async (highlightId) => {
    if (!book || !book.isbn || !highlightId) return [];

    try {
      const bookIdentifier = (book.isbn || '').split(' ')[0];
      // orderBy 없이 먼저 시도 (인덱스 없이도 작동)
      try {
        const repliesQuery = query(
          collection(db, 'highlights'),
          where('bookId', '==', bookIdentifier),
          where('parentHighlightId', '==', highlightId),
          orderBy('createdAt', 'asc')
        );
        const repliesSnapshot = await getDocs(repliesQuery);
        const replies = repliesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // createdAt 기준으로 정렬 (클라이언트 사이드)
        return replies.sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return aDate - bDate;
        });
      } catch (orderByError) {
        // orderBy 실패 시 필터만 사용하고 클라이언트에서 정렬
        console.warn('[BookDetail] orderBy failed, using client-side sort:', orderByError);
        const repliesQuery = query(
          collection(db, 'highlights'),
          where('bookId', '==', bookIdentifier),
          where('parentHighlightId', '==', highlightId)
        );
        const repliesSnapshot = await getDocs(repliesQuery);
        const replies = repliesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // 클라이언트 사이드 정렬
        return replies.sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return aDate - bDate;
        });
      }
    } catch (error) {
      console.error('[BookDetail] Error fetching replies:', error);
      return [];
    }
  }, [book]);

  // 하이라이트별 답글 조회 useEffect
  useEffect(() => {
    if (!loadingHighlights && displayedUserHighlights.length > 0) {
      const loadReplies = async () => {
        const repliesMap = {};
        for (const highlight of displayedUserHighlights) {
          // parentHighlightId가 없는 원본 하이라이트만 답글 조회
          if (!highlight.parentHighlightId) {
            const replies = await fetchReplies(highlight.id);
            if (replies.length > 0) {
              repliesMap[highlight.id] = replies;
            }
          }
        }
        setHighlightReplies(repliesMap);
      };
      loadReplies();
    }
  }, [loadingHighlights, displayedUserHighlights, fetchReplies]);

  const handleDeleteBook = async () => {
    if (!auth.currentUser || !book || !book.id) {
      setSnackbar({ open: true, message: '사용자 또는 책 정보가 유효하지 않아 삭제할 수 없습니다.', severity: 'error' });
      return;
    }

    const userId = auth.currentUser.uid;
    const bookDocId = book.id;
    const bookIsbnForHighlights = book.isbn;

    // 삭제 확인 다이얼로그 표시 상태
    setShowDeleteDialog(true);
  };

  const handleConfirmDeleteBook = async () => {
    if (!auth.currentUser || !book || !book.id || !book.isbn) {
      setSnackbar({ open: true, message: '삭제 처리 중 오류: 정보 부족', severity: 'error' });
      setShowDeleteDialog(false);
      return;
    }
    const userId = auth.currentUser.uid;
    const bookDocId = book.id;
    const bookIsbnForQuery = book.isbn.split(' ')[0];
    const bookRef = doc(db, 'books', bookDocId);

    setShowDeleteDialog(false); // Close dialog immediately
    // Optional: Add a loading state here
    setSnackbar({ open: true, message: '책 삭제 처리 중...', severity: 'info' });

    try {
      // ✅ 1. Get the current ownershipCount from the document being deleted
      let currentOwnershipCount = 1; // Default to 1 if field doesn't exist
      const docSnapBeforeDelete = await getDoc(bookRef);
      if (docSnapBeforeDelete.exists()) {
        currentOwnershipCount = docSnapBeforeDelete.data().ownershipCount || 1;
        console.log(`[BookDetail] Ownership count before delete for ${bookDocId}: ${currentOwnershipCount}`);
      } else {
        console.warn(`[BookDetail] Document ${bookDocId} not found before deletion, cannot get ownership count. Proceeding with deletion.`);
        // If doc doesn't exist, skip count updates but proceed to delete highlights
      }
      
      // ✅ 2. Decrement ownershipCount on OTHER documents with the same ISBN
      if (docSnapBeforeDelete.exists()) { // Only update others if the original doc existed
          const booksCollectionRef = collection(db, 'books');
          const otherBooksQuery = query(
              booksCollectionRef, 
              where('isbn', '==', bookIsbnForQuery), 
              where('uid', '!=', userId) // Exclude the current user's doc
          );
          const otherDocsSnapshot = await getDocs(otherBooksQuery);
          
          if (!otherDocsSnapshot.empty) {
              const batch = writeBatch(db);
              const newCount = Math.max(0, currentOwnershipCount - 1);
              otherDocsSnapshot.forEach((docToUpdate) => {
                  console.log(`[BookDetail] Updating ownershipCount for ${docToUpdate.id} to ${newCount}`);
                  batch.update(docToUpdate.ref, { ownershipCount: newCount });
              });
              await batch.commit();
              console.log(`[BookDetail] Updated ownershipCount for ${otherDocsSnapshot.size} other documents.`);
          } else {
             console.log(`[BookDetail] No other documents found with ISBN ${bookIsbnForQuery} to update count.`);
          }
      } // End if (docSnapBeforeDelete.exists())
      
      // 3. Delete user's highlights (existing logic)
      if (bookIsbnForQuery) {
        const highlightsQuery = query(
          collection(db, 'highlights'),
          where('uid', '==', userId),
          where('bookId', '==', bookIsbnForQuery) // Ensure using cleaned ISBN
        );
        const highlightsSnapshot = await getDocs(highlightsQuery);
        if (!highlightsSnapshot.empty) {
            const deleteHighlightPromises = highlightsSnapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deleteHighlightPromises);
            console.log(`Deleted ${deleteHighlightPromises.length} highlights for user ${userId} and book ISBN ${bookIsbnForQuery}`);
        } else {
             console.log(`No highlights found for user ${userId} and book ISBN ${bookIsbnForQuery} to delete.`);
        }
      }

      // ✅ 4. Delete the book document for the current user
      if (docSnapBeforeDelete.exists()) {
          await deleteDoc(bookRef);
          console.log(`Deleted book document ${bookDocId}`);
      } // Only delete if it existed

      // 5. Update UI and provide feedback (existing logic)
      setIsInLibrary(false);
      setSnackbar({ open: true, message: '책과 관련 하이라이트가 서재에서 삭제되었습니다.', severity: 'success' });
      // Optionally navigate away, e.g., navigate('/mypage');

    } catch (error) {
      console.error('책 삭제 중 오류 발생:', error);
      setSnackbar({ open: true, message: '책 삭제 중 오류가 발생했습니다.', severity: 'error' });
      // Consider reverting UI state if needed, though deletion might be partially complete
    } finally {
      // Optional: Stop loading state here
    }
  };

  const handleMenuOpen = (event, highlight) => {
    setAnchorEl(event.currentTarget);
    setSelectedHighlight(highlight);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedHighlight here, needed for dialogs
  };

  const handleEditClick = () => {
    if (!selectedHighlight) return;
    navigate(`/highlight/edit/${selectedHighlight.id}`);
    handleMenuClose(); 
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditedText('');
    setSelectedHighlight(null); // Clear selection after dialog closes
  };

  const handleSaveEditedHighlight = async () => {
    if (!selectedHighlight || !editedText.trim()) return;

    const highlightRef = doc(db, 'highlights', selectedHighlight.id);
    try {
      await updateDoc(highlightRef, {
        text: editedText.trim(),
        searchableText: editedText.trim().toLowerCase(), // Keep searchableText updated
        updatedAt: serverTimestamp()
      });

      // Update local state
      setDisplayedUserHighlights(prev => 
        prev.map(h => h.id === selectedHighlight.id ? { ...h, text: editedText.trim() } : h)
      );
      
      setSnackbar({ open: true, message: '하이라이트가 수정되었습니다.', severity: 'success' });
      handleEditDialogClose();
    } catch (error) {
      console.error("하이라이트 수정 오류:", error);
      setSnackbar({ open: true, message: '하이라이트 수정 중 오류가 발생했습니다.', severity: 'error' });
    }
  };

  // Renamed from handleDeleteClick - Opens the delete confirmation dialog
  const handleDeleteHighlightClick = () => {
    if (!selectedHighlight) return;
    setShowDeleteHighlightDialog(true);
    handleMenuClose();
  };

  // Closes the delete confirmation dialog
  const handleCloseDeleteHighlightDialog = () => {
    setShowDeleteHighlightDialog(false);
    setSelectedHighlight(null); // Clear selection after dialog closes
  };

  // Performs the actual deletion after confirmation
  const handleConfirmDeleteHighlight = async () => {
    if (!selectedHighlight) return;
    
    const highlightRef = doc(db, 'highlights', selectedHighlight.id);
    try {
      await deleteDoc(highlightRef);
      
      // Update local state
      setDisplayedUserHighlights(prev => prev.filter(h => h.id !== selectedHighlight.id));

      setSnackbar({ open: true, message: '하이라이트가 삭제되었습니다.', severity: 'success' });
      handleCloseDeleteHighlightDialog(); // Close the confirmation dialog
    } catch (error) {
      console.error("하이라이트 삭제 오류:", error);
      setSnackbar({ open: true, message: '하이라이트 삭제 중 오류가 발생했습니다.', severity: 'error' });
    }
  };

  // --- Handlers for Share Menu ---
  const handleShareMenuOpen = (event, highlight) => {
    setShareAnchorEl(event.currentTarget);
    setSelectedHighlightForShare(highlight);
  };

  const handleShareMenuClose = () => {
    setShareAnchorEl(null);
    // Keep selectedHighlightForShare until action is complete or menu closes fully
  };

  const createShareMessage = (highlight) => {
    if (!highlight || !book || !book.isbn || !auth.currentUser) return { text: '', profileLink: '', bookLink: '' };
    
    const title = book.title || '제목 없음';
    const highlightText = highlight.text.length > 100 
      ? highlight.text.substring(0, 100) + '...' 
      : highlight.text;
    const nickname = auth.currentUser.displayName || '익명';
    const profileLink = `https://snaplog.blog/u/${auth.currentUser.uid}`;
    const canonicalBookUrl = `https://snaplog.blog/book/${book.isbn}?uid=${auth.currentUser.uid}`;
    const encodedBookUrl = encodeURI(canonicalBookUrl);

    const textMessage = `📚 ${title}\n"${highlightText}"\n– by ${nickname}\n👇 책 정보 보기\n${encodedBookUrl}`;
    
    return { text: textMessage, profileLink: profileLink, bookLink: encodedBookUrl };
  };

  const handleShareKakao = async () => {
    if (!selectedHighlightForShare || !book || !book.isbn || !auth.currentUser) {
      setSnackbar({ open: true, message: '공유할 정보를 불러올 수 없습니다.', severity: 'error' });
      handleShareMenuClose();
      return;
    }
    
    if (!window.Kakao || !window.Kakao.isInitialized()) {
      setSnackbar({ open: true, message: '카카오 공유를 사용할 수 없습니다. SDK를 확인해주세요.', severity: 'error' });
      handleShareMenuClose();
      return;
    }

    const title = book.title || '제목 없음';
    const highlightText = selectedHighlightForShare.text;
    const nickname = auth.currentUser.displayName || '익명';
    const canonicalBookUrl = `https://snaplog.blog/book/${book.isbn}?uid=${auth.currentUser.uid}`;
    const encodedBookUrl = encodeURI(canonicalBookUrl);

    const kakaoTextMessage = `" ${highlightText} "\n\n📚 ${title}\n👤${nickname}`;

    try {
      await window.Kakao.Share.sendDefault({
        objectType: 'text',
        text: kakaoTextMessage,
        link: {
          mobileWebUrl: encodedBookUrl,
          webUrl: encodedBookUrl,
        },
      });
      setSnackbar({ open: true, message: '카카오톡으로 공유했습니다.', severity: 'success' });
    } catch (error) {
      console.error('카카오 공유 실패:', error);
      setSnackbar({ open: true, message: `카카오톡 공유 중 오류: ${error.message || '알 수 없는 오류'}`, severity: 'error' });
    } finally {
      handleShareMenuClose();
      setSelectedHighlightForShare(null);
    }
  };

  const handleShareWeb = async (highlight) => {
    if (!highlight || !book || !book.title || !book.isbn || !highlight.uid) return;
    // bookDocId는 `${uid}_${isbn}`
    const bookDocId = `${highlight.uid}_${book.isbn.split(' ')[0]}`;
    const message = `"${highlight.text}"\n– 『${book.title}』\n\n더 많은 기록은👇\nhttps://snaplog.blog/book/${bookDocId}\n`;
    if (navigator.share) {
      try {
        await navigator.share({
          text: message
        });
        setSnackbar({ open: true, message: '공유되었습니다.', severity: 'success' });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Web Share 실패:', error);
          setSnackbar({ open: true, message: '공유 중 오류가 발생했습니다.', severity: 'error' });
        }
      }
    } else {
      // fallback: 클립보드 복사 또는 안내
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(message);
          setSnackbar({ open: true, message: 'Web Share 미지원: 링크를 복사했습니다.', severity: 'info' });
        } catch (err) {
          setSnackbar({ open: true, message: '공유/복사 기능을 사용할 수 없습니다.', severity: 'error' });
        }
      } else {
        alert('이 브라우저는 공유 기능을 지원하지 않습니다.');
        console.warn('Web Share API 미지원, 클립보드도 없음');
      }
    }
  };

  const handleCopyLink = async (textToCopy = null) => {
    if (!selectedHighlightForShare && !textToCopy) return;

    const message = textToCopy || createShareMessage(selectedHighlightForShare);
    
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(message);
        setSnackbar({ open: true, message: '클립보드에 복사되었습니다.', severity: 'success' });
      } catch (error) {
        console.error('클립보드 복사 실패:', error);
        setSnackbar({ open: true, message: '클립보드 복사 중 오류가 발생했습니다.', severity: 'error' });
      } finally {
        handleShareMenuClose();
        setSelectedHighlightForShare(null);
      }
    } else {
       // Fallback for browsers without clipboard API support
       setSnackbar({ open: true, message: '클립보드 API가 지원되지 않습니다.', severity: 'warning' });
       handleShareMenuClose();
       setSelectedHighlightForShare(null);
    }
  };
  // --- End Handlers for Share Menu ---

  // --- Handler for Instagram DM Share ---
  const handleShareInstagramDM = async (highlight) => {
    if (!navigator.share) {
      setSnackbar({ open: true, message: '이 브라우저에서는 웹 공유 기능을 지원하지 않습니다.', severity: 'warning' });
      return;
    }
    if (!highlight || !book || !book.isbn || !auth.currentUser) {
      setSnackbar({ open: true, message: '공유할 정보를 불러올 수 없습니다.', severity: 'error' });
      return;
    }

    const title = book.title || '제목 없음';
    const highlightText = highlight.text;
    const canonicalBookUrl = `https://snaplog.blog/book/${book.isbn}?uid=${auth.currentUser.uid}`;
    const encodedBookUrl = encodeURI(canonicalBookUrl);
    
    // Construct the text message and title (Ensure these are defined once)
    const shareText = `📚 ${title}\n\n"${highlightText}"\n\n🔗 책 정보 보기:\n${encodedBookUrl}`;
    const shareTitle = `스냅로그 하이라이트: ${title}`;

    try {
      await navigator.share({
        title: shareTitle, // Use the variable defined above
        text: shareText,  // Use the variable defined above
      });
      setSnackbar({ open: true, message: '공유 옵션을 열었습니다.', severity: 'info' });
    } catch (error) { 
      if (error.name !== 'AbortError') {
        console.error('Web Share API Error:', error);
        setSnackbar({ open: true, message: '공유 중 오류가 발생했습니다.', severity: 'error' });
      }
    } 
  };
  // --- End Instagram DM Share Handler ---

  // --- 페이지 이동 핸들러 (새로 추가 또는 기존 핸들러 수정) ---
  const handleNavigateToCreateHighlight = () => {
    if (!currentUser) {
      // 비로그인 상태일 때 로그인 유도 다이얼로그 표시
      setShowLoginDialog(true);
      return;
    }
    
    if (!book || !book.isbn) {
       console.error("[BookDetail] Book data (ISBN) is not available to navigate to create highlight page.");
       setSnackbar({ open: true, message: '책 정보를 불러오는 중입니다.', severity: 'info' });
       return;
    }
    const cleanIsbn = book.isbn.split(' ')[0];
    // HighlightCreatePage 에는 isbn 만 전달
    navigate(`/highlight/create/${cleanIsbn}`, { state: { book: {
        title: book.title,
        authors: book.authors,
        thumbnail: book.thumbnail,
        isbn: cleanIsbn
    } } }); 
  };
  // --- End 페이지 이동 핸들러 ---

  // 로그인 필요 시 호출될 함수 추가
  const handleLoginRequired = async () => {
    try {
      const result = await handleGoogleLogin(
        // 성공 콜백
        (user) => {
          setSnackbar({ open: true, message: '로그인 되었습니다.', severity: 'success' });
        },
        // 오류 콜백
        (error) => {
          console.error('로그인 오류:', error);
          if (error.code === 'auth/popup-closed-by-user') {
            setSnackbar({ open: true, message: '로그인 팝업이 닫혔습니다.', severity: 'warning' });
          } else {
            setSnackbar({ open: true, message: '로그인 중 오류가 발생했습니다.', severity: 'error' });
          }
        },
        // 인앱 브라우저 감지 콜백
        () => {
          setInAppBrowserModalOpen(true);
        }
      );
    } catch (error) {
      console.error('로그인 프로세스 오류:', error);
      setSnackbar({ open: true, message: '로그인 중 오류가 발생했습니다.', severity: 'error' });
    }
  };

  // 정렬 토글 핸들러
  const handleToggleHighlightSort = () => {
    setHighlightSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  // 정렬된 하이라이트 배열 생성
  const sortedUserHighlights = React.useMemo(() => {
    if (!displayedUserHighlights) return [];
    return [...displayedUserHighlights].sort((a, b) => {
      const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt;
      const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt;
      if (!aDate || !bDate) return 0;
      return highlightSortOrder === 'desc'
        ? bDate - aDate
        : aDate - bDate;
    });
  }, [displayedUserHighlights, highlightSortOrder]);

  // 날짜 포맷 함수 (YYYY.MM.DD)
  const formatHighlightDate = (dateObj) => {
    if (!dateObj) return '';
    let d = dateObj;
    if (typeof d.toDate === 'function') d = d.toDate();
    if (!(d instanceof Date)) d = new Date(d);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  // 템플릿 생성 함수 분리
  const generateBlogTemplate = () => {
    if (!book || !highlightAuthorProfile) return '';
    const title = book.title || '';
    const authors = book.authors?.join(', ') || '';
    const thumbnail = book.thumbnail || '';
    const nickname = highlightAuthorProfile.displayName || '익명';
    const userCode = highlightAuthorUid || (currentUser && currentUser.uid) || '';
    const bookId = book.id || '';
    const bookContent = book.contents || '';
    
    // 하이라이트와 날짜를 묶어서 오름차순 정렬
    const highlightsWithDate = displayedUserHighlights
      .map((h, i) => ({ text: h.text, createdAt: h.createdAt }))
      .filter(h => !!h.text)
      .sort((a, b) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt;
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt;
        if (!aDate || !bDate) return 0;
        return aDate - bDate; // 오름차순
      });
    
    let template = '';
    
    // 책 제목
    template += `${title}\n`;

    // 책 정보
    if (bookContent) {
      template += `${bookContent}\n\n`;
    }
    if (authors) {
      template += `저자: ${authors}\n\n`;
    }
    
    // 메모 (있는 경우에만)
    if (book.memo && book.memo.trim()) {
      template += `메모\n${book.memo.trim()}\n\n`;
    }
    
    // 읽은 기간
    const readingPeriods = formatReadingPeriods(book);
    if (readingPeriods && readingPeriods.texts.length > 0) {
      readingPeriods.texts.forEach((text, index) => {
        template += `읽은 기간\n${text}\n`;
      });
      template += `\n`;
    }
    
    // 태그 (최소한의 이모지만 사용)
    const tags = [];
    
    // 별점
    if (book?.starRating) {
      tags.push(`★${book.starRating}`);
    }
    
    // 추천도
    if (book?.recommendation) {
      const recLabels = {
        '필독': '필독',
        '추천': '추천', 
        '보통': '보통',
        '비추천': '비추천'
      };
      tags.push(recLabels[book.recommendation] || book.recommendation);
    }
    
    // 감정 & 느낌
    if (Array.isArray(book?.emotions) && book.emotions.length > 0) {
      book.emotions.forEach(emo => {
        tags.push(emo);
      });
    }
    
    // 소장 여부
    const ownershipData = book?.ownershipStatus || book?.genres || [];
    if (Array.isArray(ownershipData) && ownershipData.length > 0) {
      ownershipData.forEach(ownership => {
        tags.push(ownership);
      });
    }
    
    // 읽은 이유
    const purposeData = book?.readingPurpose || (Array.isArray(book?.mustRead) ? book?.mustRead : (book?.mustRead ? [book?.mustRead] : []));
    if (Array.isArray(purposeData) && purposeData.length > 0) {
      purposeData.forEach(purpose => {
        tags.push(purpose);
      });
    }
    
    if (tags.length > 0) {
      template += `${tags.join('  ')}\n\n`;
    }
    
    
    // 하이라이트
    if (highlightsWithDate.length > 0) {
      template += `---\n\n`;
      highlightsWithDate.forEach((h) => {
        const dateStr = formatHighlightDate(h.createdAt);
        template += `"${h.text}"\n`;
        template += `${dateStr}\n\n\n`;
      });
    }
    
    // 푸터
    template += `---\n\n`;
    template += `이 글은 ${nickname}님의 스냅로그 기록이에요\n`;
    template += `${nickname}님의 기록이 더 궁금하다면\n`;
    template += `https://snaplog.blog/u/${userCode}\n`;
    
    return template;
  };

  const handleMoveToBlog = () => {
    const template = generateBlogTemplate();
    const isAndroid = /Android/i.test(navigator.userAgent);
    if (isAndroid) {
      // 인텐트에 EXTRA_POST_TEXT로 본문 전달
      const encodedText = encodeURIComponent(template);
      window.location.href =
        `intent://write#Intent;scheme=naverblog;package=com.nhn.android.blog;S.EXTRA_POST_TEXT=${encodedText};S.browser_fallback_url=https://blog.naver.com;end;`;
    } else {
      window.open('https://blog.naver.com', '_blank');
    }
  };

  const handleBlogTemplateCopy = async () => {
    const template = generateBlogTemplate();
    try {
      await navigator.clipboard.writeText(template);
      setSnackbar({ open: true, message: '템플릿이 복사되었습니다. 붙여넣기(Ctrl+V) 해보세요.', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: '복사 실패: 클립보드 접근 불가', severity: 'error' });
    }
  };

  const handleShareBookTemplate = async () => {
    const template = generateBlogTemplate();
    if (!template) {
      setSnackbar({ open: true, message: '공유할 템플릿 내용이 없습니다.', severity: 'error' });
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({
          text: template
        });
        setSnackbar({ open: true, message: '공유되었습니다.', severity: 'success' });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Web Share 실패:', error);
          setSnackbar({ open: true, message: '공유 중 오류가 발생했습니다.', severity: 'error' });
        }
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(template);
        setSnackbar({ open: true, message: 'Web Share 미지원: 템플릿을 복사했습니다.', severity: 'info' });
      } catch (err) {
        setSnackbar({ open: true, message: '공유/복사 기능을 사용할 수 없습니다.', severity: 'error' });
      }
    } else {
      alert('이 브라우저는 공유 기능을 지원하지 않습니다.');
      setSnackbar({ open: true, message: '공유/복사 기능을 사용할 수 없습니다.', severity: 'error' });
      console.warn('Web Share API, 클립보드 모두 미지원');
    }
  };

  useEffect(() => {
    console.log('📚 [가격 fetch 시작]');
    if (!book) {
      console.warn('⛔ book이 없습니다.');
      return;
    }
  
    if (book.priceSales || book.priceStandard) {
      console.log('✅ 이미 가격 정보가 존재합니다.');
      setBookPrice({
        priceSales: book.priceSales,
        priceStandard: book.priceStandard
      });
      return;
    }
  
    // ISBN 목록 추출
    let isbnList = [];
    if (book?.isbn) {
      isbnList = book.isbn.split(' ');
      console.log('🔎 book.isbn에서 추출한 isbnList:', isbnList);
    } else if (idFromParams && idFromParams.includes('_')) {
      isbnList = [idFromParams.split('_')[1]];
      console.log('🔎 idFromParams에서 추출한 isbnList:', isbnList);
    } else {
      console.warn('⛔ ISBN 추출 실패: book.isbn도 없고 idFromParams도 없음');
    }
  
    // ISBN-10 → ISBN-13 변환 함수
    function isbn10to13(isbn10) {
      if (!isbn10 || isbn10.length !== 10) return null;
      let core = isbn10.slice(0, 9);
      let isbn13 = '978' + core;
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        sum += parseInt(isbn13[i]) * (i % 2 === 0 ? 1 : 3);
      }
      let checkDigit = (10 - (sum % 10)) % 10;
      return isbn13 + checkDigit;
    }
  
    // 13자리 우선 추출
    let isbn = null;
    for (const candidate of isbnList) {
      if (candidate.length === 13) {
        isbn = candidate;
        console.log('✅ 13자리 ISBN 사용됨:', isbn);
        break;
      }
    }
  
    // 10자리 → 변환
    if (!isbn) {
      for (const candidate of isbnList) {
        if (candidate.length === 10) {
          isbn = isbn10to13(candidate);
          if (isbn) {
            console.log('🔄 ISBN-10 → ISBN-13 변환됨:', isbn);
            break;
          }
        }
      }
    }
  
    if (!isbn || isbn.length !== 13) {
      console.warn('⛔ 유효한 ISBN13이 없습니다.');
      setPriceError('유효한 ISBN13이 없습니다.');
      return;
    }
  
    // 가격 요청
    const fetchPrice = async () => {
      try {
        console.log('🚀 가격 정보 fetch 요청 시작:', isbn);
        const response = await fetch(`/api/get-aladin-price?isbn=${isbn}`);
        console.log('📥 fetch 응답 status:', response.status);
        if (!response.ok) throw new Error('API 요청 실패');
  
        const data = await response.json();
        console.log('📦 API 응답 내용:', data);
  
        if (!data || (!data.priceSales && !data.priceStandard)) {
          setPriceError('가격 정보를 불러올 수 없습니다.');
          return;
        }
  
        setBookPrice({ priceSales: data.priceSales, priceStandard: data.priceStandard });
      } catch (e) {
        console.error('❌ fetchPrice 오류:', e);
        setPriceError('가격 정보를 불러올 수 없습니다.');
      }
    };
  
    fetchPrice();
  }, [book, idFromParams]);

  // 페이지 진입 시 스크롤을 최상단으로 초기화
  useEffect(() => {
    // highlight 파라미터가 없으면 항상 최상단으로 스크롤
    if (!highlightParam) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [idFromParams]); // idFromParams가 변경될 때마다 (새 페이지 진입 시)

  // 하이라이트 렌더링 후 highlight 파라미터로 스크롤 (개선된 버전)
  useEffect(() => {
    if (highlightParam && !loadingHighlights) {
      // 브라우저 호환성을 위한 스크롤 함수
      const smoothScrollToElement = (element, options = {}) => {
        const {
          behavior = 'smooth',
          block = 'center',
          inline = 'nearest'
        } = options;

        // 모던 브라우저 지원
        if (element.scrollIntoView && typeof element.scrollIntoView === 'function') {
          try {
            element.scrollIntoView({ behavior, block, inline });
            return true;
          } catch (error) {
            console.warn('scrollIntoView failed, falling back to manual scroll:', error);
          }
        }

        // 폴백: 수동 스크롤 계산
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2);
        
        window.scrollTo({
          top: middle,
          behavior: behavior === 'smooth' ? 'smooth' : 'auto'
        });
        
        return true;
      };

      // 더 안정적인 스크롤을 위한 여러 단계 시도
      const scrollToHighlight = () => {
        const el = document.getElementById(`highlight-${highlightParam}`);
        if (el) {
          // 부드러운 스크롤 애니메이션
          const scrollSuccess = smoothScrollToElement(el, {
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
          
          if (scrollSuccess) {
            // 스크롤 완료 후 하이라이트 카드에 시각적 강조 효과
            setTimeout(() => {
              // CSS 변수를 사용한 애니메이션 (더 안정적)
              el.style.setProperty('--scale', '1.01');
              el.style.setProperty('--shadow', '0px 8px 24px rgba(0, 0, 0, 0.12), 0px 12px 32px rgba(0, 0, 0, 0.08)');
              el.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
              el.style.transform = 'scale(var(--scale, 1))';
              el.style.boxShadow = 'var(--shadow, 0px 1px 3px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.03))';
              
              // 1초 후 원래 상태로 복원
              setTimeout(() => {
                el.style.setProperty('--scale', '1');
                el.style.setProperty('--shadow', '0px 1px 3px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.03)');
                el.style.transform = 'scale(var(--scale, 1))';
                el.style.boxShadow = 'var(--shadow, 0px 1px 3px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.03))';
              }, 1000);
            }, 500);
          }
          
          return true;
        }
        return false;
      };

      // 즉시 시도
      if (!scrollToHighlight()) {
        // 실패 시 약간의 딜레이 후 재시도 (렌더링 완료 대기)
        setTimeout(() => {
          if (!scrollToHighlight()) {
            // 두 번째 재시도 (더 긴 딜레이)
            setTimeout(scrollToHighlight, 200);
          }
        }, 100);
      }
    }
  }, [highlightParam, loadingHighlights, displayedUserHighlights, otherHighlights]);

  // 스크롤 위치 복원 기능 제거 - 새 페이지 진입 시 항상 최상단에서 시작
  // (뒤로가기/앞으로가기는 아래의 handlePopState에서 처리)

  // 브라우저 뒤로가기/앞으로가기 시 스크롤 위치 유지 (개선된 버전)
  useEffect(() => {
    let isNavigating = false;

    const handleBeforeUnload = () => {
      // 페이지를 떠날 때 현재 스크롤 위치 저장
      if (!isNavigating) {
        sessionStorage.setItem('bookDetailScrollPosition', window.scrollY.toString());
      }
    };

    const handlePopState = () => {
      isNavigating = true;
      // 브라우저 뒤로가기/앞으로가기 시 저장된 스크롤 위치 복원
      const savedPosition = sessionStorage.getItem('bookDetailScrollPosition');
      if (savedPosition) {
        const position = parseInt(savedPosition);
        if (!isNaN(position)) {
          setTimeout(() => {
            try {
              window.scrollTo({
                top: position,
                behavior: 'instant' // 즉시 이동 (애니메이션 없음)
              });
            } catch (error) {
              // 폴백: 즉시 스크롤
              window.scrollTo(0, position);
            }
          }, 50);
        }
      }
      
      // 네비게이션 완료 후 플래그 리셋
      setTimeout(() => {
        isNavigating = false;
      }, 100);
    };

    // 터치 디바이스에서 스크롤 성능 최적화
    const handleTouchStart = () => {
      document.body.style.overflow = 'auto';
    };

    const handleTouchEnd = () => {
      // 터치 종료 후 약간의 딜레이로 스크롤 최적화
      setTimeout(() => {
        document.body.style.overflow = '';
      }, 100);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // 메모 저장
  const handleSaveMemo = async () => {
    if (!book || !book.id) return;
    setSavingMemo(true);
    try {
      const bookRef = doc(db, 'books', book.id);
      await updateDoc(bookRef, { memo: memoInput });
      setMemoText(memoInput);
      setEditingMemo(false);
      setSnackbar({ open: true, message: '메모가 저장되었습니다.', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: '메모 저장 실패', severity: 'error' });
    } finally {
      setSavingMemo(false);
    }
  };

  // Google Fonts 글로벌 스타일 적용
  const memoFontGlobal = (
    <>
      <Global styles={`
        @font-face {
          font-family: 'BMHANNAAir_otf';
          src: url('/fonts/BMHANNAAir_otf.otf') format('opentype');
          font-weight: normal;
          font-style: normal;
        }
        @font-face {
          font-family: 'BMHANNA_11yrs_oft';
          src: url('/fonts/BMHANNA_11yrs_oft.oft') format('opentype');
          font-weight: normal;
          font-style: normal;
        }
        @import url('https://fonts.googleapis.com/css2?family=Nanum+Pen+Script&family=Caveat:wght@400;700&display=swap');
      `} />
    </>
  );

  const sheetButtonSx = {
    width: '100%',
    justifyContent: 'flex-start',
    px: 2,
    py: 2,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#000',
    borderRadius: 0,
    minHeight: 48,
    borderBottom: '1px solid #eee',
    textTransform: 'none',
    fontFamily: "'NanumSquareBold', sans-serif",
    '&:last-of-type': { borderBottom: 'none' }
  };

  // 태그 스타일 생성 함수 - 인스타그램 해시태그 느낌 (단순 텍스트)
  const createChipStyle = (color, isSelected = true) => {
    return {
      px: 0, 
      py: 0,
      fontWeight: 400,
      fontSize: '0.8125rem',
      cursor: 'default',
      boxShadow: 'none',
      bgcolor: 'transparent',
      color: '#333333',
      border: 'none',
      mb: 0,
      mr: 0,
      minWidth: 'auto',
      textAlign: 'left',
      userSelect: 'none',
      display: 'inline',
      fontFamily: 'NanumSquareR, sans-serif',
      opacity: 0.9,
    };
  };

  // 이미지 내보내기 모달 상태 (이제 페이지 이동으로 대체)
  // const [imageExportModalOpen, setImageExportModalOpen] = useState(false);
  // const [selectedHighlightForExport, setSelectedHighlightForExport] = useState(null);

  const handleOpenImageExportModal = (highlight) => {
    // setSelectedHighlightForExport(highlight);
    // setImageExportModalOpen(true);
    navigate('/export/highlight', {
      state: {
        highlight: highlight,
        book: book,
        authorProfile: highlightAuthorProfile,
      },
    });
  };

  // 썸네일 우선순위: userThumbnail > thumbnail > 기본 이미지
  const getBookThumbnail = (book) => {
    if (!book) return '/images/no-cover.png';
    if (book.thumbnail) return book.thumbnail;
    return '/images/no-cover.png';
  };

  // 오픈그래프용 이미지 URL 생성 함수
  const getOpenGraphImage = (book) => {
    if (!book) return '/op-image.png';
    
    // 1. 사용자가 직접 등록한 이미지가 있으면 우선 사용
    if (isValidImageUrl(book.userThumbnail)) {
      return normalizeImageUrl(book.userThumbnail);
    }
    
    // 2. 책의 섬네일이 있으면 사용 (유효한 URL인지 확인)
    if (isValidImageUrl(book.thumbnail)) {
      return normalizeImageUrl(book.thumbnail);
    }
    
    // 3. 기본 이미지 사용
    return '/op-image.png';
  };

  // 이미지 URL 유효성 검사 함수
  const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    
    // 빈 문자열이나 기본 이미지 경로는 제외
    if (url.trim() === '' || url === '/images/no-cover.png') return false;
    
    // URL 형식 검사
    try {
      new URL(url);
      return true;
    } catch {
      // 상대 경로인 경우도 유효하다고 간주
      return url.startsWith('/') || url.startsWith('./');
    }
  };

  // 이미지 로딩 에러 핸들러
  const handleImageError = () => {
    setImageLoadError(true);
    setOgImageUrl('/op-image.png');
  };

  // 이미지 URL 정규화 함수
  const normalizeImageUrl = (url) => {
    if (!url) return '/op-image.png';
    
    // 상대 경로인 경우 절대 경로로 변환
    if (url.startsWith('/')) {
      return url;
    }
    
    // 외부 URL인 경우 그대로 반환
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // 기본 이미지 반환
    return '/op-image.png';
  };

  // 날짜별로 하이라이트를 그룹화하는 함수
  const groupHighlightsByDate = (highlights) => {
    const groups = {};
    highlights.forEach(h => {
      let d = h.createdAt;
      if (d && typeof d.toDate === 'function') d = d.toDate();
      if (!(d instanceof Date)) d = new Date(d);
      if (isNaN(d.getTime())) return;
      const dateStr = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
      if (!groups[dateStr]) groups[dateStr] = [];
      groups[dateStr].push(h);
    });
    // 날짜 내림차순 정렬
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  };

  // 읽은 기간을 간결하게 표시하는 함수
  const formatReadingPeriods = (book) => {
    if (!book) return null;
    
    const formatDate = (date) => {
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
    };
    
    // 다중 읽은 기간 데이터 확인
    if (book.readingPeriods && Array.isArray(book.readingPeriods) && book.readingPeriods.length > 0) {
      const validPeriods = book.readingPeriods
        .filter(period => period.startDate && (period.startDate.toDate || period.startDate instanceof Date))
        .map(period => {
          const startDate = period.startDate.toDate ? period.startDate.toDate() : new Date(period.startDate);
          const endDate = period.endDate ? (period.endDate.toDate ? period.endDate.toDate() : new Date(period.endDate)) : null;
          
          return {
            startDate,
            endDate,
            startDateStr: formatDate(startDate),
            endDateStr: endDate ? formatDate(endDate) : null,
            days: endDate ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1 : null,
            isCompleted: !!endDate
          };
        })
        .sort((a, b) => b.startDate - a.startDate); // 내림차순 정렬 (최신 기간이 위로)
      
      if (validPeriods.length === 0) return null;
      
      // 모든 기간을 텍스트로 변환
      const periodTexts = validPeriods.map(period => {
        if (period.isCompleted) {
          if (period.days === 1) {
            return `${period.startDateStr} (1일)`;
          } else {
            return `${period.startDateStr} ~ ${period.endDateStr} (${period.days}일)`;
          }
        } else {
          // 아직 읽는 중
          const today = new Date();
          const daysSinceStart = Math.ceil((today - period.startDate) / (1000 * 60 * 60 * 24));
          
          if (daysSinceStart === 0) {
            return `${period.startDateStr} (오늘부터)`;
          } else if (daysSinceStart === 1) {
            return `${period.startDateStr} (어제부터)`;
          } else {
            return `${period.startDateStr} (${daysSinceStart}일째)`;
          }
        }
      });
      
      return {
        texts: periodTexts,
        periods: validPeriods,
        hasMultiplePeriods: validPeriods.length > 1
      };
    }
    
    // 기존 단일 기간 데이터 (호환성)
    if (book.startedAt) {
      const startDate = book.startedAt.toDate ? book.startedAt.toDate() : new Date(book.startedAt);
      const endDate = book.finishedAt ? (book.finishedAt.toDate ? book.finishedAt.toDate() : new Date(book.finishedAt)) : null;
      
      const startDateStr = formatDate(startDate);
      
      if (endDate) {
        const endDateStr = formatDate(endDate);
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        
        let periodText = '';
        if (daysDiff === 1) {
          periodText = `${startDateStr} (1일)`;
        } else {
          periodText = `${startDateStr} ~ ${endDateStr} (${daysDiff}일)`;
        }
        
        return {
          texts: [periodText],
          periods: [{
            startDate,
            endDate,
            startDateStr,
            endDateStr,
            days: daysDiff,
            isCompleted: true
          }],
          hasMultiplePeriods: false
        };
      } else {
        // 아직 읽는 중
        const today = new Date();
        const daysSinceStart = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
        
        let periodText = '';
        if (daysSinceStart === 0) {
          periodText = `${startDateStr} (오늘부터)`;
        } else if (daysSinceStart === 1) {
          periodText = `${startDateStr} (어제부터)`;
        } else {
          periodText = `${startDateStr} (${daysSinceStart}일째)`;
        }
        
        return {
          texts: [periodText],
          periods: [{
            startDate,
            endDate: null,
            startDateStr,
            endDateStr: null,
            days: null,
            isCompleted: false
          }],
          hasMultiplePeriods: false
        };
      }
    }
    
    return null;
  };

  // 날짜 문자열(YYYY.MM.DD) → 'N일 전', 'N달 전', 'N년 전' 등 상대적 시간으로 변환
  const getRelativeDateString = (dateStr) => {
    const [year, month, day] = dateStr.split('.').map(Number);
    const date = new Date(year, month - 1, day);
    const now = new Date();
    // 오늘 0시 기준으로 비교
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffMs = today - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return '오늘';
    if (diffDays === 1) return '1일 전';
    if (diffDays < 30) return `${diffDays}일 전`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths}달 전`;
    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears}년 전`;
  };

  // 바텀시트 상태 추가
  const [highlightBottomSheetOpen, setHighlightBottomSheetOpen] = useState(false);
  const [selectedHighlightForBottomSheet, setSelectedHighlightForBottomSheet] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [ogImageUrl, setOgImageUrl] = useState('/op-image.png');

  // 활성화된 하이라이트 카드 ID (1회 탭 시 옵션 버튼 표시용)
  const [activeHighlightId, setActiveHighlightId] = useState(null);

  // 답글 관련 상태
  const [highlightReplies, setHighlightReplies] = useState({}); // { highlightId: [replies] }
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [savingReply, setSavingReply] = useState(false);
  const [replyingToHighlightId, setReplyingToHighlightId] = useState(null);
  const [replyingToHighlight, setReplyingToHighlight] = useState(null); // 답글 작성 중인 하이라이트 정보 저장
  const [showReplyInput, setShowReplyInput] = useState({}); // { highlightId: true/false } - 인라인 답글 입력 표시 여부
  const [inlineReplyText, setInlineReplyText] = useState({}); // { highlightId: 'text' } - 인라인 답글 텍스트
  const [selectedReplyId, setSelectedReplyId] = useState(null); // 선택된 답글 ID (삭제 버튼 표시용)
  
  // 답글 textarea refs (성능 최적화)
  const replyTextareaRefs = useRef({}); // { highlightId: textareaElement }

  // book 데이터가 변경될 때 오픈그래프 이미지 URL 업데이트
  useEffect(() => {
    if (book) {
      const newOgImageUrl = getOpenGraphImage(book);
      setOgImageUrl(newOgImageUrl);
      
      // 이미지 로딩 에러 상태 리셋
      setImageLoadError(false);
      
      // 이미지 유효성 사전 검사 (선택적)
      if (newOgImageUrl && newOgImageUrl !== '/op-image.png') {
        const img = new Image();
        img.onload = () => {
          // 이미지 로딩 성공
        };
        img.onerror = () => {
          // 이미지 로딩 실패 시 기본 이미지로 폴백
          setOgImageUrl('/op-image.png');
        };
        img.src = newOgImageUrl;
      }
    }
  }, [book]);

  // 부드러운 스크롤 유틸리티 함수
  const smoothScrollToElement = (element, options = {}) => {
    const {
      behavior = 'smooth',
      block = 'center',
      inline = 'nearest'
    } = options;

    // 모던 브라우저 지원
    if (element.scrollIntoView && typeof element.scrollIntoView === 'function') {
      try {
        element.scrollIntoView({ behavior, block, inline });
        return true;
      } catch (error) {
        console.warn('scrollIntoView failed, falling back to manual scroll:', error);
      }
    }

    // 폴백: 수동 스크롤 계산
    const elementRect = element.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const middle = absoluteElementTop - (window.innerHeight / 2);
    
    window.scrollTo({
      top: middle,
      behavior: behavior === 'smooth' ? 'smooth' : 'auto'
    });
    
    return true;
  };

  // 하이라이트 카드 1회 탭: 활성화 + 스크롤 / 재클릭 시 비활성화
  const handleHighlightCardClick = (highlight) => {
    const highlightId = highlight.id;

    if (activeHighlightId === highlightId) {
      setActiveHighlightId(null);
      return;
    }

    setActiveHighlightId(highlightId);
    const el = document.getElementById(`highlight-${highlightId}`);
    if (el) {
      const scrollSuccess = smoothScrollToElement(el, {
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });

      if (scrollSuccess) {
        setTimeout(() => {
          el.style.setProperty('--scale', '1.01');
          el.style.setProperty('--shadow', '0px 8px 24px rgba(0, 0, 0, 0.12), 0px 12px 32px rgba(0, 0, 0, 0.08)');
          el.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          el.style.transform = 'scale(var(--scale, 1))';
          el.style.boxShadow = 'var(--shadow, 0px 1px 3px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.03))';
          setTimeout(() => {
            el.style.setProperty('--scale', '1');
            el.style.setProperty('--shadow', '0px 1px 3px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.03)');
            el.style.transform = 'scale(var(--scale, 1))';
            el.style.boxShadow = 'var(--shadow, 0px 1px 3px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.03))';
          }, 1000);
        }, 500);
      }
    }
  };

  // 옵션 버튼 클릭: 바텀시트 열기 (stopPropagation으로 카드 클릭 중복 방지)
  const handleOpenOptionsBottomSheet = (highlight, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSelectedHighlightForBottomSheet(highlight);
    setHighlightBottomSheetOpen(true);
  };

  // 바텀시트 닫기 핸들러
  const handleCloseHighlightBottomSheet = () => {
    setHighlightBottomSheetOpen(false);
    setSelectedHighlightForBottomSheet(null);
  };

  // 바텀시트에서 이미지 내보내기
  const handleBottomSheetImageExport = () => {
    if (selectedHighlightForBottomSheet) {
      handleOpenImageExportModal(selectedHighlightForBottomSheet);
    }
    handleCloseHighlightBottomSheet();
  };

  // 바텀시트에서 공유하기
  const handleBottomSheetShare = () => {
    if (selectedHighlightForBottomSheet) {
      handleShareWeb(selectedHighlightForBottomSheet);
    }
    handleCloseHighlightBottomSheet();
  };

  // 바텀시트에서 수정하기
  const handleBottomSheetEdit = () => {
    if (selectedHighlightForBottomSheet) {
      navigate(`/highlight/edit/${selectedHighlightForBottomSheet.id}`);
    }
    handleCloseHighlightBottomSheet();
  };

  // 바텀시트에서 삭제하기
  const handleBottomSheetDelete = () => {
    if (selectedHighlightForBottomSheet) {
      setSelectedHighlight(selectedHighlightForBottomSheet);
      setShowDeleteHighlightDialog(true);
    }
    handleCloseHighlightBottomSheet();
  };

  // 바텀시트에서 답글 작성하기
  const handleBottomSheetReply = () => {
    if (selectedHighlightForBottomSheet) {
      setReplyingToHighlightId(selectedHighlightForBottomSheet.id);
      setReplyingToHighlight(selectedHighlightForBottomSheet); // 원문장 표시를 위해 저장
      setReplyText('');
      setReplyDialogOpen(true);
    }
    handleCloseHighlightBottomSheet();
  };

  // 인라인 답글 작성 토글
  const handleToggleReplyInput = (highlightId, e) => {
    if (e) {
      e.stopPropagation(); // 카드 클릭 이벤트 방지
    }
    setShowReplyInput(prev => ({
      ...prev,
      [highlightId]: !prev[highlightId]
    }));
    if (!showReplyInput[highlightId]) {
      setReplyingToHighlightId(highlightId);
      // 해당 하이라이트 정보 찾기
      const targetHighlight = displayedUserHighlights.find(h => h.id === highlightId);
      if (targetHighlight) {
        setReplyingToHighlight(targetHighlight);
      }
    } else {
      setInlineReplyText(prev => ({ ...prev, [highlightId]: '' }));
    }
  };

  // 답글 입력 핸들러 (성능 최적화 - requestAnimationFrame으로 배치 업데이트)
  const handleReplyTextInput = useCallback((highlightId, e) => {
    const newValue = e.target.value;
    // requestAnimationFrame을 사용하여 업데이트를 다음 프레임으로 지연
    // 이렇게 하면 입력 중에는 리렌더링이 최소화됨
    requestAnimationFrame(() => {
      setInlineReplyText(prev => {
        // 이전 값과 동일하면 업데이트하지 않음
        if (prev[highlightId] === newValue) {
          return prev;
        }
        return {
          ...prev,
          [highlightId]: newValue
        };
      });
    });
  }, []);

  // 인라인 답글 저장
  const handleSaveInlineReply = async (highlightId) => {
    // ref에서 직접 값 읽기 (성능 최적화)
    const textarea = replyTextareaRefs.current[highlightId];
    const text = textarea ? textarea.value.trim() : (inlineReplyText[highlightId]?.trim() || '');
    if (!text || !currentUser || !book) {
      setSnackbar({ open: true, message: '답글을 입력해주세요.', severity: 'warning' });
      return;
    }

    setSavingReply(true);
    try {
      const bookIdentifier = (book.isbn || '').split(' ')[0];
      const replyData = {
        bookId: bookIdentifier,
        bookTitle: book.title || '제목 없음',
        text: text,
        searchableText: text.toLowerCase(),
        uid: currentUser.uid,
        parentHighlightId: highlightId,
        userName: currentUser.displayName || '익명',
        userPhotoURL: currentUser.photoURL || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'highlights'), replyData);

      // 답글 목록 새로고침
      const updatedReplies = await fetchReplies(highlightId);
      setHighlightReplies(prev => ({
        ...prev,
        [highlightId]: updatedReplies
      }));

      setSnackbar({ open: true, message: '답글이 작성되었습니다.', severity: 'success' });
      // textarea 초기화
      const textarea = replyTextareaRefs.current[highlightId];
      if (textarea) {
        textarea.value = '';
      }
      setShowReplyInput(prev => ({ ...prev, [highlightId]: false }));
      setInlineReplyText(prev => ({ ...prev, [highlightId]: '' }));
      setReplyingToHighlightId(null);
      setReplyingToHighlight(null);
    } catch (error) {
      console.error('답글 저장 오류:', error);
      setSnackbar({ open: true, message: '답글 저장 중 오류가 발생했습니다.', severity: 'error' });
    } finally {
      setSavingReply(false);
    }
  };

  // 답글 삭제 핸들러 (bookdetail 작성자만)
  const handleDeleteReply = async (replyId, parentHighlightId) => {
    if (!isHighlightOwner) {
      setSnackbar({ open: true, message: '답글을 삭제할 권한이 없습니다.', severity: 'warning' });
      return;
    }

    try {
      const replyRef = doc(db, 'highlights', replyId);
      await deleteDoc(replyRef);

      // 답글 목록 새로고침
      const updatedReplies = await fetchReplies(parentHighlightId);
      setHighlightReplies(prev => ({
        ...prev,
        [parentHighlightId]: updatedReplies
      }));

      setSnackbar({ open: true, message: '답글이 삭제되었습니다.', severity: 'success' });
    } catch (error) {
      console.error('답글 삭제 오류:', error);
      setSnackbar({ open: true, message: '답글 삭제 중 오류가 발생했습니다.', severity: 'error' });
    }
  };

  // 답글 저장 핸들러
  const handleSaveReply = async () => {
    if (!replyText.trim() || !replyingToHighlightId || !currentUser || !book) {
      setSnackbar({ open: true, message: '답글을 입력해주세요.', severity: 'warning' });
      return;
    }

    setSavingReply(true);
    try {
      const bookIdentifier = (book.isbn || '').split(' ')[0];
      const replyData = {
        bookId: bookIdentifier,
        bookTitle: book.title || '제목 없음',
        text: replyText.trim(),
        searchableText: replyText.trim().toLowerCase(),
        uid: currentUser.uid,
        parentHighlightId: replyingToHighlightId,
        userName: currentUser.displayName || '익명',
        userPhotoURL: currentUser.photoURL || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const newReplyDoc = await addDoc(collection(db, 'highlights'), replyData);

      // 답글 목록 새로고침 (즉시 반영)
      const updatedReplies = await fetchReplies(replyingToHighlightId);
      setHighlightReplies(prev => ({
        ...prev,
        [replyingToHighlightId]: updatedReplies
      }));

      setSnackbar({ open: true, message: '답글이 작성되었습니다.', severity: 'success' });
      setReplyDialogOpen(false);
      setReplyText('');
      setReplyingToHighlightId(null);
      setReplyingToHighlight(null);
      
      // 인라인 답글 입력도 닫기
      setShowReplyInput(prev => ({ ...prev, [replyingToHighlightId]: false }));
      setInlineReplyText(prev => ({ ...prev, [replyingToHighlightId]: '' }));
    } catch (error) {
      console.error('답글 저장 오류:', error);
      setSnackbar({ open: true, message: '답글 저장 중 오류가 발생했습니다.', severity: 'error' });
    } finally {
      setSavingReply(false);
    }
  };

  if (loading && !book) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
         <TopBar title="오류" showBack={true} />
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!book) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
         <TopBar title="정보 없음" showBack={true} />
        <Typography>책 정보를 찾을 수 없습니다.</Typography>
      </Box>
    );
  }

  // 하이라이트 섹션 제목 결정
  const highlightsSectionTitle = "하이라이트";

  return (
    <>
      <SEO 
        title={book ? `${book.title} | 스냅로그` : "책 상세 | 스냅로그"}
        description={book ? `${book.title}${book.authors ? ` - ${book.authors.join(', ')}` : ''} 독서기록을 확인하세요.` : "독서기록을 확인하세요."}
        bookTitle={book?.title}
        bookAuthor={book?.authors?.join(', ')}
        image={ogImageUrl}
        url={`/book/${idFromParams}`}
        type="article"
      />
      
      {/* JSON-LD 구조화된 데이터 추가 */}
      {book && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateStructuredData(book, highlightAuthorProfile, displayedUserHighlights))
          }}
        />
      )}
      
      {memoFontGlobal}
      <Box sx={{ pb: 7, maxWidth: '600px', margin: '0 auto', width: '100%'}}>
        <TopBar 
          title={book.title || "내 서재 책"}
          showBack={true}
          position="static"
          onBack={() => {
            if (idFromParams && idFromParams.includes('_')) {
              const [uid] = idFromParams.split('_');
              navigate(`/u/${uid}`);
            } else {
              navigate('/mypage');
            }
          }}
          rightContent={
            currentUser && book && book.uid === currentUser.uid ? (
              <Tooltip title="내 서재에서 삭제">
                <IconButton 
                  onClick={handleDeleteBook} 
                  size="small"
                  sx={{ 
                    color: '#ff8a80',
                    '&:hover': {
                      bgcolor: 'rgba(255, 138, 128, 0.08)',
                      color: '#ff6b6b',
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            ) : null
          }
        />

        {/* 삭제 확인 다이얼로그 */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
        >
          <DialogTitle>책 삭제 확인</DialogTitle>
          <DialogContent>
            <Typography>
              이 책을 내 서재에서 삭제하시겠습니까? 삭제 시, 이 책에 대해 작성하신 모든 하이라이트도 함께 삭제되며 복구할 수 없습니다.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>취소</Button>
            <Button 
              onClick={handleConfirmDeleteBook}
              color="error"
              variant="contained"
            >
              삭제
            </Button>
          </DialogActions>
        </Dialog>

        {/* 책 정보 */}
        <Box sx={{ p: 0 }}>
          <Card 
            sx={{ 
              boxShadow: 'none',   
              borderRadius: 0,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              bgcolor: '#f9f9f9',
            }} 
            elevation={0}          
          >
            <CardContent sx={{ display: 'flex', gap: 2, pt: 3, pb: 2.5, width: '100%', maxWidth: '100%', minWidth: 0 }}>
              <Box
                component="img"
                src={getBookThumbnail(book)}
                alt={`${book.title}${book.authors ? ` - ${book.authors.join(', ')}` : ''}`}
                loading="lazy"
                sx={{
                  width: 80,
                  height: 114,
                  objectFit: 'cover',
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderTopRightRadius: 8,
                  borderBottomRightRadius: 8,
                  boxShadow: '6px 3px 15px 0 rgba(0,0,0,0.15), 8px 8px 20px 0 rgba(0,0,0,0.08)'
                }}
              />
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%', minWidth: 0 }}>
                <Typography variant="h6" sx={{ 
                  mb: 1.5,
                  fontWeight: 700,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                  width: '100%',
                  maxWidth: '100%',
                  fontSize: '1.125rem',
                  lineHeight: 1.4,
                  fontFamily: 'NanumSquareB, sans-serif',
                  color: '#1a1a1a',
                }}>{book.title}</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      fontFamily: 'NanumSquareR, sans-serif',
                      color: '#666666',
                    }}
                  >
                    {book.authors?.join(', ') || '작가 정보 없음'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'right',
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      fontFamily: 'NanumSquareR, sans-serif',
                      color: '#666666',
                    }}
                  >
                    {book.publisher || '출판사 정보 없음'}
                  </Typography>
                </Box>
                {/* 가격 정보 + 더보기 버튼 */}
                {bookPrice && bookPrice.priceSales && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 600, 
                      fontSize: '1rem',
                      fontFamily: 'NanumSquareB, sans-serif',
                      color: '#1a1a1a',
                    }}>
                      {`${Number(bookPrice.priceSales).toLocaleString()}원`}
                    </Typography>
                    <IconButton size="small" onClick={() => setPriceSheetOpen(true)} sx={{ ml: 1, p: 0.5 }} aria-label="가격 더보기">
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </CardContent>
            {book.contents && !memoText && (
              <Typography
                variant="body2"
                sx={{
                  mx: 2.5,
                  mt: 1.5,
                  mb: 2,
                  minWidth: 0,
                  maxWidth: '100%',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.7,
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  fontFamily: 'NanumSquareR, sans-serif',
                  color: '#1a1a1a',
                }}
              >
                {book.contents}
              </Typography>
            )}
          </Card>
          {/* --- 메모 박스 (자연스럽고 심플한 디자인) --- */}
          {book && (
            <Box
              sx={{
                bgcolor: 'transparent',
                px: 2.5,
                pt: 1.5,
                pb: 2,
                minHeight: 60,
                cursor: currentUser && book.uid === currentUser.uid ? 'pointer' : 'default',
                position: 'relative',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 2,
                ...(currentUser && book.uid === currentUser.uid && {
                  '&:hover': {
                    '& .memo-icon': {
                      transform: 'rotate(0deg) scale(1.05)',
                      filter: 'grayscale(0)',
                    },
                    '& .memo-text': {
                      color: '#2a2a2a',
                    }
                  },
                })
              }}
              onClick={currentUser && book.uid === currentUser.uid ? () => navigate(`/book/${book.id}/memo-edit`) : undefined}
            >
              {/* 메모 텍스트 영역 */}
              <Box
                className="memo-text"
                sx={{
                  minHeight: 40,
                  fontSize: memoText ? '0.9375rem' : '0.875rem',
                  fontWeight: 400,
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  mb: 1.5,
                  color: memoText ? '#1a1a1a' : '#999999',
                  fontFamily: 'NanumSquareR, sans-serif',
                  letterSpacing: '0.01em',
                  lineHeight: 1.7,
                  background: 'none',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  zIndex: 2,
                }}
              >
                {memoText ? (
                  <Box component="span" sx={{ display: 'block', width: '100%' }}>{memoText}</Box>
                ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      color: '#999999',
                      ...(currentUser && book.uid === currentUser.uid && {
                        '&:hover': { color: '#FFD700' },
                        transition: 'color 0.3s ease',
                      })
                    }}>
                      <Box component="span" sx={{ flex: 1 }}>
                        {currentUser && book.uid === currentUser.uid ? '메모를 작성해보세요' : '메모 없음'}
                      </Box>
                    </Box>
                )}
              </Box>
              {/* 읽은 기간 + 읽기 상태 + 태그 칩 영역 */}
              <Box sx={{ 
                mt: 1.5,
                pt: 1.5,
                opacity: memoText ? 1 : 0.8,
                transition: 'opacity 0.3s ease',
              }}>
                {/* 읽은 기간 표시 */}
                {(() => {
                  const readingPeriods = formatReadingPeriods(book);
                  if (readingPeriods) {
                    return (
                      <Box sx={{ 
                        mb: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}>
                        {readingPeriods.texts.map((text, index) => (
                          <Typography key={index} sx={{
                            fontSize: '0.8125rem',
                            color: '#666666',
                            fontFamily: 'NanumSquareR, sans-serif',
                            fontWeight: 400,
                            lineHeight: 1.5,
                          }}>
                            {text}
                          </Typography>
                        ))}
                      </Box>
                    );
                  }
                  return null;
                })()}
                
                {/* 태그 칩 렌더링 - BookMemoEdit의 모든 태그 반영 */}
                {(() => {
                  const chips = [];
                  
                  // 1. 별점 (⭐)
                  if (book?.starRating) {
                    chips.push(
                        <Box key="star" component="span" sx={{
                          ...createChipStyle('#FFD700'),
                        }}>{`★${book.starRating}`}</Box>
                    );
                  }
                  
                  // 2. 추천도 (👍) - 노란색 계열로 통일
                  if (book?.recommendation) {
                    const recItems = [
                      { label: '💖 필독', value: '필독', color: '#FFD700' },
                      { label: '👍 추천', value: '추천', color: '#FFD700' },
                      { label: '🤷‍♀️ 보통', value: '보통', color: '#FFD700' },
                      { label: '👎 비추천', value: '비추천', color: '#FFA000' },
                    ];
                    const recItem = recItems.find(item => item.value === book.recommendation);
                    if (recItem) {
                      chips.push(
                        <Box key="rec" component="span" sx={{
                          ...createChipStyle(recItem.color),
                        }}>{recItem.label}</Box>
                      );
                    }
                  }
                  
                  // 3. 감정 & 느낌 (💭) - 노란색 계열로 통일
                  if (Array.isArray(book?.emotions) && book.emotions.length > 0) {
                    const emoItems = [
                      { label: '🔥 몰입', value: '몰입', color: '#FFD700' },
                      { label: '💡 통찰', value: '통찰', color: '#FFD700' },
                      { label: '😊 유쾌', value: '유쾌', color: '#FFD700' },
                      { label: '😴 지루', value: '지루', color: '#FFA000' },
                      { label: '😤 답답', value: '답답', color: '#FFA000' },
                    ];
                    book.emotions.forEach((emo, idx) => {
                      const emoItem = emoItems.find(item => item.value === emo);
                      if (emoItem) {
                        chips.push(
                          <Box key={`emo${idx}`} component="span" sx={{
                            ...createChipStyle(emoItem.color),
                          }}>{emoItem.label}</Box>
                        );
                      }
                    });
                  }
                  
                  // 4. 소장 여부 (📚) - 노란색 계열로 통일
                  const ownershipData = book?.ownershipStatus || book?.genres || [];
                  if (Array.isArray(ownershipData) && ownershipData.length > 0) {
                    const ownershipItems = [
                      { label: '📦 소장 중', value: '소장중', color: '#FFD700' },
                      { label: '💸 읽고 판매함', value: '읽고판매함', color: '#FFA000' },
                      { label: '📖 빌려 읽음', value: '빌려읽음', color: '#FFD700' },
                      { label: '🎁 선물받음', value: '선물받음', color: '#FFD700' },
                      { label: '💰 구매 예정', value: '구매예정', color: '#FFA000' },
                    ];
                    ownershipData.forEach((ownership, idx) => {
                      const ownershipItem = ownershipItems.find(item => item.value === ownership);
                      if (ownershipItem) {
                        chips.push(
                          <Box key={`ownership${idx}`} component="span" sx={{
                            ...createChipStyle(ownershipItem.color),
                          }}>{ownershipItem.label}</Box>
                        );
                      }
                    });
                  }
                  
                  // 5. 읽은 이유 (🎯) - 노란색 계열로 통일
                  const purposeData = book?.readingPurpose || (Array.isArray(book?.mustRead) ? book?.mustRead : (book?.mustRead ? [book?.mustRead] : []));
                  if (Array.isArray(purposeData) && purposeData.length > 0) {
                    const purposeItems = [
                      { label: '💪 성장', value: '성장', color: '#FFD700' },
                      { label: '❤️ 힐링', value: '힐링', color: '#FFD700' },
                      { label: '🌍 세계관 확장', value: '세계관확장', color: '#FFD700' },
                      { label: '👁️ 견문 넓히기', value: '견문넓히기', color: '#FFD700' },
                      { label: '💼 업무 도움', value: '업무도움', color: '#FFD700' },
                      { label: '🧠 지식 습득', value: '지식습득', color: '#FFD700' },
                      { label: '🎭 재미/오락', value: '재미오락', color: '#FFD700' },
                      { label: '🔍 호기심 해결', value: '호기심해결', color: '#FFD700' },
                    ];
                    purposeData.forEach((purpose, idx) => {
                      const purposeItem = purposeItems.find(item => item.value === purpose);
                      if (purposeItem) {
                        chips.push(
                          <Box key={`purpose${idx}`} component="span" sx={{
                            ...createChipStyle(purposeItem.color),
                          }}>{purposeItem.label}</Box>
                        );
                      }
                    });
                  }
                  
                  // 태그가 있으면 구분선과 함께 표시, 없으면 상태 텍스트만 표시
                  if (chips.length > 0) {
                    return (
                      <>
                        <Box sx={{ 
                          borderTop: '1px solid #f0f0f0', 
                          pt: 1.5, 
                          mb: 1.5 
                        }}>
                          <Typography sx={{
                            fontSize: '0.8125rem',
                            color: '#999999',
                            fontFamily: 'NanumSquareR, sans-serif',
                            fontWeight: 400,
                            mb: 1,
                          }}>
                            {statusBadge}
                          </Typography>
                        </Box>
                        <Box sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: '0.5rem',
                          width: '100%',
                          lineHeight: 1.6,
                        }}>
                          {chips.map((chip, idx) => (
                            <React.Fragment key={idx}>
                              {chip}
                              {idx < chips.length - 1 && (
                                <Box component="span" sx={{ color: '#cccccc', fontSize: '0.75rem', mx: 0.25 }}>·</Box>
                              )}
                            </React.Fragment>
                          ))}
                        </Box>
                      </>
                    );
                  } else {
                    return (
                      <Typography sx={{
                        fontSize: '0.8125rem',
                        color: '#999999',
                        fontFamily: 'NanumSquareR, sans-serif',
                        fontWeight: 400,
                      }}>
                        {statusBadge}
                      </Typography>
                    );
                  }
                })()}
              </Box>
            </Box>
          )}
          {/* --- 메모 박스 끝 --- */}
          {/* 가격 비교 바텀시트 Dialog */}
          <Dialog
            open={priceSheetOpen}
            onClose={() => setPriceSheetOpen(false)}
            fullWidth
            maxWidth="xs"
            PaperProps={{
              sx: {
                position: 'fixed',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                m: 0,
                width: '100%',
                maxWidth: '600px',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                textAlign: 'center',
                p: 3,
                boxShadow: 8,
                borderRadius: '16px',
                bgcolor: '#fff',
                overflow: 'visible',
              }
            }}
          >
            <DialogTitle sx={{ pt: 3, pb: 1, fontWeight: 600, fontSize: '1.15rem', textAlign: 'center' }}>가격 비교</DialogTitle>
            <DialogContent>
              <Box sx={{ py: 1, fontSize: '1.08rem', fontWeight: 500 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <span>알라딘 판매가</span>
                  <span>{bookPrice?.priceSales ? `${Number(bookPrice.priceSales).toLocaleString()}원` : '-'}</span>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <span>정가</span>
                  <span>{bookPrice?.priceStandard ? `${Number(bookPrice.priceStandard).toLocaleString()}원` : '-'}</span>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                {/* 알라딘 구매 버튼 */}
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  href={book?.aladinLink || (book?.itemId ? `https://www.aladin.co.kr/shop/wproduct.aspx?ItemId=${book.itemId}` : undefined)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 600, fontSize: '1.08rem' }}
                  disabled={!book?.itemId && !book?.aladinLink}
                >
                  알라딘에서 구매하기
                </Button>
                {/* YES24 검색 버튼 */}
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  size="large"
                  href={`https://search.yes24.com/Search?domain=BOOK&query=${encodeURIComponent(book?.title || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 600, fontSize: '1.08rem' }}
                >
                  YES24에서 검색하기
                </Button>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 1 }}>
              <Button onClick={() => setPriceSheetOpen(false)} sx={{ color: 'text.secondary' }}>닫기</Button>
            </DialogActions>
          </Dialog>
        </Box>

        {/* ✅ 수정된 하이라이트 섹션 */}
        {(highlightsSectionTitle !== null) && ( 
          <>
            <Divider sx={{ mb: 3, mt: 0 }} />
            <Box sx={{ mb: 3, px: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2 }}>
                <Typography variant="h7" sx={{ 
                  fontWeight: 700, 
                  fontSize: '1.0625rem',
                  fontFamily: 'NanumSquareB, sans-serif',
                  color: '#1a1a1a',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                }}>
                  {isHighlightOwner
                    ? `기록한 문장 ${displayedUserHighlights.length}개`
                    : `${highlightAuthorProfile?.displayName || '사용자'}님의 기록 (${displayedUserHighlights.length})`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* 정렬 토글 버튼 */}
                  <Tooltip title={highlightSortOrder === 'desc' ? '최신순' : '오래된순'}>
                    <IconButton
                      size="small"
                      onClick={handleToggleHighlightSort}
                      sx={{ 
                        color: '#666666',
                        '&:hover': {
                          color: '#333333',
                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                      aria-label="정렬 토글"
                    >
                      <SwapVertIcon sx={{ transform: highlightSortOrder === 'desc' ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
                    </IconButton>
                  </Tooltip>
                  {/* 블로그 복사하기 버튼 */}
                  <Tooltip title="블로그 복사하기">
                    <IconButton
                      size="small"
                      onClick={handleBlogTemplateCopy}
                      sx={{ 
                        color: '#666666',
                        '&:hover': {
                          color: '#333333',
                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                      aria-label="블로그 복사하기"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* 공유하기 버튼 */}
                  <Tooltip title="공유하기">
                    <IconButton
                      size="small"
                      onClick={handleShareBookTemplate}
                      sx={{ 
                        color: '#666666',
                        '&:hover': {
                          color: '#333333',
                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                      aria-label="공유하기"
                    >
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {loadingHighlights ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  {sortedUserHighlights.length > 0 ? (
                    groupHighlightsByDate(sortedUserHighlights).map(([date, highlights]) => (
                      <Box key={date} sx={{ mb: 0 }}>
                        <Box sx={{ 
                          fontWeight: 700, 
                          fontSize: '1.125rem', 
                          fontFamily: 'NanumSquareB, sans-serif',
                          color: '#1a1a1a', 
                          mb: 1.5, 
                          mt: 3, 
                          letterSpacing: '-0.01em', 
                          px: 2 
                        }}>
                          {getRelativeDateString(date)}
                        </Box>
                        {highlights.map(highlight => {
                          // 썸네일 이미지 경로
                          const thumbnail = getBookThumbnail(book);
                          // 작성자명
                          const displayName = isHighlightOwner ? undefined : (highlightAuthorProfile?.displayName || '익명');
                          // 답글 조회
                          const replies = highlightReplies[highlight.id] || [];
                          return (
                            <Paper
                              key={highlight.id}
                              id={`highlight-${highlight.id}`} // 스크롤을 위한 ID 추가
                              elevation={0}
                              sx={{
                                position: 'relative',
                                width: '100%',
                                maxWidth: 'none',
                                minWidth: 0,
                                bgcolor: '#fff',
                                border: 'none',
                                boxShadow: 'none',
                                overflow: 'hidden',
                                px: 0,
                                mx: 0,
                                my: 0,
                                py: 0,
                                mb: 3,
                                cursor: 'pointer',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                border: activeHighlightId === highlight.id ? '1px solid rgba(0,0,0,0.08)' : '1px solid transparent',
                                boxShadow: activeHighlightId === highlight.id ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                                '&:hover': {
                                  opacity: 0.95,
                                },
                                display: 'block',
                                boxSizing: 'border-box',
                              }}
                              onClick={() => handleHighlightCardClick(highlight)}
                            >
                              {/* 활성화 시: 검은색 투명 오버레이 + 중앙 옵션 버튼 */}
                              {activeHighlightId === highlight.id && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(0,0,0,0.35)',
                                    backdropFilter: 'blur(1px)',
                                    animation: 'fadeIn 0.2s ease forwards',
                                    '@keyframes fadeIn': { to: { opacity: 1 } },
                                  }}
                                  onClick={(e) => { e.stopPropagation(); setActiveHighlightId(null); }}
                                >
                                  <IconButton
                                    aria-label="문장 옵션"
                                    onClick={(e) => handleOpenOptionsBottomSheet(highlight, e)}
                                    sx={{
                                      minWidth: 48,
                                      minHeight: 48,
                                      width: 48,
                                      height: 48,
                                      color: '#fff',
                                      bgcolor: 'rgba(255,255,255,0.15)',
                                      border: '1px solid rgba(255,255,255,0.3)',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                      backdropFilter: 'blur(8px)',
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        transform: 'scale(1.05)',
                                      },
                                    }}
                                  >
                                    <MoreVertIcon sx={{ fontSize: 24 }} />
                                  </IconButton>
                                </Box>
                              )}
                              {/* 북타이틀 + 섬네일 (상단) */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  px: { xs: 2.5, sm: 3 },
                                  pt: { xs: 2.5, sm: 3 },
                                  pb: { xs: 1.5, sm: 2 },
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: 'NanumSquareR, sans-serif',
                                    fontWeight: 600,
                                    fontSize: '0.8125rem',
                                    lineHeight: 1.4,
                                    color: '#777666',
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    wordBreak: 'break-word',
                                    pr: 1.5,
                                    letterSpacing: '-0.01em',
                                  }}
                                >
                                  {book.title}
                                </Typography>
                                <Box
                                  sx={{
                                    width: 40,
                                    minHeight: 56, // 책 표지 비율 (약 5:7)
                                    maxHeight: 56,
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                    bgcolor: '#f8f8f8',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={thumbnail}
                                    alt={`${book.title}${book.authors ? ` - ${book.authors.join(', ')}` : ''}`}
                                    loading="lazy"
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      objectPosition: 'center',
                                      display: 'block',
                                    }}
                                  />
                                </Box>
                              </Box>
                              
                              {/* 하이라이트 본문 */}
                              <Box
                                sx={{
                                  width: '100%',
                                  pt: 0,
                                  pb: { xs: 1.5, sm: 2 },
                                  pl: { xs: 2.5, sm: 3 },
                                  pr: { xs: 2.5, sm: 3 },
                                  boxSizing: 'border-box',
                                  zIndex: 2,
                                  background: 'transparent',
                                  minHeight: 0,
                                }}
                              >
                                <FormattedTextDisplay 
                                  formattedText={highlight.formattedText || highlight.text}
                                  sx={{
                                    fontFamily: 'NanumSquareR, sans-serif',
                                    fontWeight: 400,
                                    fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
                                    letterSpacing: '0.01em',
                                    lineHeight: 1.8,
                                    color: '#1a1a1a',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                />
                              </Box>
                              
                              {/* 답글 작성 버튼 */}
                              {currentUser && (
                                <Box
                                  sx={{
                                    pl: { xs: 2.5, sm: 3 },
                                    pr: { xs: 2.5, sm: 3 },
                                    pt: { xs: 1, sm: 1.5 },
                                    pb: { xs: 1, sm: 1.5 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                  }}
                                  onClick={(e) => handleToggleReplyInput(highlight.id, e)}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: 'NanumSquareR, sans-serif',
                                      fontSize: '0.8125rem',
                                      color: '#999999',
                                      cursor: 'pointer',
                                      fontWeight: 400,
                                      '&:hover': {
                                        color: '#666666',
                                      },
                                    }}
                                  >
                                    답글 작성
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* 인라인 답글 작성 UI (들여쓰기) */}
                              {currentUser && showReplyInput[highlight.id] && (
                                <Box
                                  sx={{
                                    pl: { xs: 4.5, sm: 5.5 },
                                    pr: { xs: 2.5, sm: 3 },
                                    pb: replies.length > 0 ? 1.5 : 2,
                                    pt: 1,
                                    position: 'relative',
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Box
                                    sx={{
                                      bgcolor: 'transparent',
                                      border: 'none',
                                      borderRadius: 0,
                                      p: 0,
                                      position: 'relative',
                                    }}
                                  >
                                    <textarea
                                      ref={(el) => {
                                        if (el) {
                                          replyTextareaRefs.current[highlight.id] = el;
                                          // 초기 값 설정
                                          if (inlineReplyText[highlight.id]) {
                                            el.value = inlineReplyText[highlight.id];
                                          }
                                        }
                                      }}
                                      autoFocus
                                      rows={3}
                                      placeholder="답글을 입력하세요..."
                                      defaultValue={inlineReplyText[highlight.id] || ''}
                                      onChange={(e) => handleReplyTextInput(highlight.id, e)}
                                      style={{
                                        width: '100%',
                                        fontFamily: 'NanumSquareR, sans-serif',
                                        fontSize: '0.875rem',
                                        lineHeight: 1.6,
                                        color: '#1a1a1a',
                                        background: 'transparent',
                                        border: 'none',
                                        borderBottom: '1px solid #e8e8e8',
                                        borderRadius: 0,
                                        outline: 'none',
                                        padding: '8px 0',
                                        resize: 'none',
                                        boxSizing: 'border-box',
                                        margin: 0,
                                        WebkitAppearance: 'none',
                                        appearance: 'none',
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.borderBottomColor = '#333';
                                      }}
                                      onBlur={(e) => {
                                        e.target.style.borderBottomColor = '#e8e8e8';
                                      }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1.5 }}>
                                      <Button
                                        size="small"
                                        onClick={() => {
                                          const textarea = replyTextareaRefs.current[highlight.id];
                                          if (textarea) {
                                            textarea.value = '';
                                          }
                                          setShowReplyInput(prev => ({ ...prev, [highlight.id]: false }));
                                          setInlineReplyText(prev => ({ ...prev, [highlight.id]: '' }));
                                          setReplyingToHighlightId(null);
                                          setReplyingToHighlight(null);
                                        }}
                                        sx={{
                                          color: '#999999',
                                          fontSize: '0.8125rem',
                                          textTransform: 'none',
                                          fontWeight: 400,
                                          minWidth: 'auto',
                                          px: 1.5,
                                          '&:hover': {
                                            color: '#666666',
                                            bgcolor: 'transparent',
                                          },
                                        }}
                                      >
                                        취소
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => handleSaveInlineReply(highlight.id)}
                                        disabled={!inlineReplyText[highlight.id]?.trim() || savingReply}
                                        sx={{
                                          color: '#333',
                                          fontSize: '0.8125rem',
                                          textTransform: 'none',
                                          fontWeight: 500,
                                          minWidth: 'auto',
                                          px: 1.5,
                                          '&:hover': {
                                            color: '#1a1a1a',
                                            bgcolor: 'transparent',
                                          },
                                          '&:disabled': {
                                            color: '#cccccc',
                                          },
                                        }}
                                      >
                                        {savingReply ? '작성 중...' : '작성'}
                                      </Button>
                                    </Box>
                                  </Box>
                                </Box>
                              )}
                              
                              {/* 답글 목록 (들여쓰기) */}
                              {replies.length > 0 && (
                                <Box sx={{ 
                                  pl: { xs: 4.5, sm: 5.5 },
                                  pr: { xs: 2.5, sm: 3 },
                                  pb: { xs: 2, sm: 2.5 },
                                  pt: currentUser && showReplyInput[highlight.id] ? 1 : (currentUser ? 1 : 1.5),
                                  position: 'relative',
                                }}>
                                  {replies.map((reply, replyIndex) => (
                                    <Box
                                      key={reply.id}
                                      sx={{
                                        position: 'relative',
                                        width: '100%',
                                        bgcolor: 'transparent',
                                        border: 'none',
                                        borderRadius: 0,
                                        p: 0,
                                        mb: replyIndex < replies.length - 1 ? 1.5 : 0,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                          opacity: 0.8,
                                        },
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // 답글 클릭 시 선택 상태 토글
                                        if (selectedReplyId === reply.id) {
                                          setSelectedReplyId(null);
                                        } else {
                                          setSelectedReplyId(reply.id);
                                        }
                                      }}
                                    >
                                      {/* 답글 텍스트 */}
                                      <FormattedTextDisplay 
                                        formattedText={reply.formattedText || reply.text}
                                        sx={{
                                          fontFamily: 'NanumSquareR, sans-serif',
                                          fontWeight: 400,
                                          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                                          letterSpacing: '0.01em',
                                          lineHeight: 1.75,
                                          color: '#666666',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                          mb: selectedReplyId === reply.id && isHighlightOwner ? 0.75 : 0,
                                        }}
                                      />
                                      {/* 답글 삭제 버튼 (클릭 시에만 표시, bookdetail 작성자만) */}
                                      {selectedReplyId === reply.id && isHighlightOwner && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 0.75 }}>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteReply(reply.id, highlight.id);
                                              setSelectedReplyId(null);
                                            }}
                                            sx={{
                                              color: '#999999',
                                              p: 0.5,
                                              minWidth: 'auto',
                                              width: 24,
                                              height: 24,
                                              '&:hover': {
                                                color: '#ff6b6b',
                                                bgcolor: 'rgba(255, 107, 107, 0.08)',
                                              },
                                            }}
                                          >
                                            <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                                          </IconButton>
                                        </Box>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Paper>
                          );
                        })}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2, pt: 2, textAlign: 'center' }}>
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </>
        )}

        {/* ✅ 다른 사람의 하이라이트 섹션: URL에 uid 쿼리가 없을 때만 표시 */}
        {!queryParamUid && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ px: 2 }}>
              <Typography variant="h6" sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: '1.0625rem',
                fontFamily: 'NanumSquareB, sans-serif',
                color: '#1a1a1a',
              }}>다른 사람의 문장</Typography>
              {loadingHighlights ? (
                <CircularProgress size={24} />
              ) : otherHighlights.length > 0 ? (
                otherHighlights.map(highlight => (
                  <Paper
                    key={highlight.id}
                    id={`highlight-${highlight.id}`} // 스크롤을 위한 ID 추가
                    elevation={0}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: 'none',
                      minWidth: 0,
                      bgcolor: '#FFFFFF',
                      borderRadius: '12px',
                      border: activeHighlightId === highlight.id ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(0, 0, 0, 0.06)',
                      boxShadow: activeHighlightId === highlight.id ? '0px 4px 12px rgba(0, 0, 0, 0.08)' : '0px 1px 3px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.03)',
                      overflow: 'hidden',
                      px: 0,
                      mx: 0,
                      my: 0,
                      py: 0,
                      mb: 2,
                      cursor: 'pointer',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08), 0px 8px 24px rgba(0, 0, 0, 0.04)',
                        borderColor: 'rgba(0, 0, 0, 0.1)',
                        transform: 'translateY(-1px)',
                      },
                      display: 'block',
                      boxSizing: 'border-box',
                    }}
                    onClick={() => handleHighlightCardClick(highlight)}
                  >
                    {/* 활성화 시: 검은색 투명 오버레이 + 중앙 옵션 버튼 */}
                    {activeHighlightId === highlight.id && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.35)',
                          backdropFilter: 'blur(1px)',
                          animation: 'fadeIn 0.2s ease forwards',
                          '@keyframes fadeIn': { to: { opacity: 1 } },
                        }}
                        onClick={(e) => { e.stopPropagation(); setActiveHighlightId(null); }}
                      >
                        <IconButton
                          aria-label="문장 옵션"
                          onClick={(e) => handleOpenOptionsBottomSheet(highlight, e)}
                          sx={{
                            minWidth: 48,
                            minHeight: 48,
                            width: 48,
                            height: 48,
                            color: '#fff',
                            bgcolor: 'rgba(255,255,255,0.15)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            backdropFilter: 'blur(8px)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.25)',
                              borderColor: 'rgba(255,255,255,0.5)',
                              transform: 'scale(1.05)',
                            },
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Box>
                    )}
                    {/* 하이라이트 텍스트 */}
                    <Box
                      sx={{
                        width: { xs: '100%', sm: '100%' },
                        pt: { xs: '24px', sm: '28px' },
                        pl: { xs: '20px', sm: '28px' },
                        pr: { xs: '20px', sm: '28px' },
                        boxSizing: 'border-box',
                        zIndex: 2,
                        background: 'transparent',
                        minHeight: 0,
                      }}
                    >
                      <FormattedTextDisplay 
                        formattedText={highlight.formattedText || highlight.text}
                        sx={{
                          fontFamily: 'NanumSquareR, sans-serif',
                          fontWeight: 400,
                          fontSize: { xs: '0.9375rem', sm: '1rem' },
                          letterSpacing: '0.01em',
                          lineHeight: 1.75,
                          color: '#1a1a1a',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      />
                    </Box>
                    {/* 하단 정보 그룹 */}
                    <Box
                      sx={{
                        position: 'relative',
                        left: 'auto',
                        right: 'auto',
                        bottom: 'auto',
                        mt: 2.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '2px',
                        zIndex: 2,
                        pl: { xs: '20px', sm: '28px' },
                        pr: { xs: '20px', sm: '28px' },
                        mb: 2.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'NanumSquareR, sans-serif',
                          fontWeight: 400,
                          fontSize: '0.8125rem',
                          lineHeight: 1.4,
                          color: '#999999',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {highlight.createdAt && (typeof highlight.createdAt.toDate === 'function'
                          ? highlight.createdAt.toDate().toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\./g, '.').replace(/\s/g, '')
                          : new Date(highlight.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\./g, '.').replace(/\s/g, '')
                        )}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'NanumSquareR, sans-serif',
                          fontWeight: 400,
                          fontSize: '0.875rem',
                          lineHeight: 1.4,
                          color: '#666666',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mt: 0.5,
                        }}
                      >
                        {book.title}
                      </Typography>
                    </Box>
                    {/* 썸네일 이미지 (카드 하단에 항상 붙도록) */}
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 16,
                        bottom: 0,
                        width: 58.83,
                        height: 59.3,
                        borderRadius: '6px 6px 0 0',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12), 0px 4px 12px rgba(0, 0, 0, 0.08)',
                        overflow: 'hidden',
                        zIndex: 3,
                        bgcolor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        transition: 'box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                                                <Box
                            component="img"
                            src={getBookThumbnail(book)}
                            alt={`${book.title}${book.authors ? ` - ${book.authors.join(', ')}` : ''}`}
                            loading="lazy"
                            sx={{
                              width: '100%',
                              height: '135%',
                              objectFit: 'cover',
                              objectPosition: 'top',
                              display: 'block',
                              background: '#eee',
                            }}
                          />
                    </Box>
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">다른 사용자의 문장이 없습니다.</Typography>
              )}
            </Box>
          </>
        )}

        {/* 하이라이트 액션 메뉴 (수정/삭제) */}
        <Dialog
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          fullWidth
          PaperProps={{
            sx: {
              position: 'fixed',
              m: 0,
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxWidth: '600px',
              width: '100%',
              boxShadow: 8,
              pb: 2
            }
          }}
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
          keepMounted
        >
          <DialogTitle sx={{ textAlign: 'center', fontWeight: 600, fontSize: '1.1rem', pb: 1 }}>문장 옵션</DialogTitle>
          <DialogContent sx={{ p: 0, width: '100%', boxSizing: 'border-box' }}>
            <Button
              fullWidth
              onClick={handleEditClick}
              startIcon={<EditIcon />}
              sx={{ px: 2, py: 2, fontSize: '1.1rem', fontWeight: 500, color: 'text.primary', justifyContent: 'flex-start', borderBottom: '1px solid #eee' }}
            >
              수정
            </Button>
            <Button
              fullWidth
              onClick={handleDeleteHighlightClick}
              startIcon={<DeleteIcon />}
              sx={{ px: 2, py: 2, fontSize: '1.1rem', fontWeight: 500, color: 'error.main', justifyContent: 'flex-start' }}
            >
              삭제
            </Button>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 1 }}>
            <Button onClick={handleMenuClose} sx={{ color: 'text.secondary' }}>닫기</Button>
          </DialogActions>
        </Dialog>

        {/* 하이라이트 공유 메뉴 */}
        <Menu
          anchorEl={shareAnchorEl}
          open={Boolean(shareAnchorEl)}
          onClose={handleShareMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleShareKakao} disabled={!window.Kakao || !window.Kakao.isInitialized()}>
            <ChatBubbleOutlineIcon fontSize="small" sx={{ mr: 1 }} /> 카카오톡 공유
          </MenuItem>
          <MenuItem onClick={() => handleShareWeb(selectedHighlightForShare)} disabled={!navigator.share}>
            <IosShareIcon fontSize="small" sx={{ mr: 1 }} /> 다른 앱으로 공유
          </MenuItem>
          <MenuItem onClick={() => handleCopyLink()}>
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> 내용 복사하기
          </MenuItem>
        </Menu>

        {/* 하이라이트 수정 다이얼로그 */}
        <Dialog open={editDialogOpen} onClose={handleEditDialogClose} fullWidth maxWidth="sm">
          <DialogTitle>문장 수정</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="edit-highlight-text"
              label="문장 내용"
              type="text"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditDialogClose}>취소</Button>
            <Button onClick={handleSaveEditedHighlight} variant="contained">
              저장
            </Button>
          </DialogActions>
        </Dialog>

        {/* 하이라이트 삭제 확인 다이얼로그 */}
        <Dialog
          open={showDeleteHighlightDialog}
          onClose={handleCloseDeleteHighlightDialog}
        >
          <DialogTitle>문장 삭제 확인</DialogTitle>
          <DialogContent>
            <Typography>이 문장을 정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteHighlightDialog}>취소</Button>
            <Button onClick={handleConfirmDeleteHighlight} color="error">
              삭제
            </Button>
          </DialogActions>
        </Dialog>

        {/* 인앱 브라우저 감지 모달 */}
        <InAppBrowserModal 
          open={inAppBrowserModalOpen} 
          onClose={() => setInAppBrowserModalOpen(false)} 
        />
        
        {/* 로그인 유도 다이얼로그 */}
        <Dialog
          open={showLoginDialog}
          onClose={() => setShowLoginDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              overflow: 'hidden',
              background: 'transparent',
              boxShadow: 'none',
            }
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              borderRadius: '20px',
              p: 0,
            }}
          >
            {/* 배경 이미지 */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${book?.thumbnail || '/images/home-01.png'})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                filter: 'blur(8px) brightness(0.6)',
                zIndex: 0,
              }}
            />
            
            {/* 그라데이션 오버레이 */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.8) 100%)',
                zIndex: 1,
              }}
            />

            {/* 메인 콘텐츠 */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                p: 4,
                width: '100%',
                height: '100%',
                textAlign: 'center',
              }}
            >
              {/* 책 정보 */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontFamily: 'NanumSquareEB',
                    fontSize: { xs: '1.4rem', sm: '1.6rem' },
                    fontWeight: 700,
                    color: 'white',
                    textShadow: '0 4px 12px rgba(0,0,0,0.6)',
                    lineHeight: 1.3,
                    mb: 1,
                  }}
                >
                  {book?.title || '이 책'}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'NanumSquareR',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 400,
                    textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                  }}
                >
                  {book?.authors?.join(', ') || '작가 정보 없음'}
                </Typography>
              </Box>

              {/* 메인 메시지 */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  sx={{
                    fontFamily: 'NanumSquareEB',
                    fontSize: { xs: '1.6rem', sm: '1.8rem' },
                    fontWeight: 700,
                    color: 'white',
                    textShadow: '0 4px 12px rgba(0,0,0,0.6)',
                    lineHeight: 1.3,
                    mb: 2,
                  }}
                >
                  마음에 남는 문장을<br />
                  기록해보세요
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'NanumSquareR',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontWeight: 400,
                    lineHeight: 1.6,
                    textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                  }}
                >
                  로그인하고 나만의 문장을<br />
                  아름답게 남겨보세요
                </Typography>
              </Box>

              {/* 로그인 버튼 */}
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleLoginRequired}
                  startIcon={
                    <Box
                      component="img"
                      src="data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 48 48'%3e%3cpath fill='%23EA4335' d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'/%3e%3cpath fill='%234285F4' d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'/%3e%3cpath fill='%23FBBC05' d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'/%3e%3cpath fill='%2334A853' d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'/%3e%3cpath fill='none' d='M0 0h48v48H0z'/%3e%3c/svg%3e"
                      alt="Google"
                      sx={{ width: 20, height: 20 }}
                    />
                  }
                  sx={{
                    bgcolor: 'white',
                    color: '#333',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    px: { xs: 4, sm: 5 },
                    py: { xs: 1.5, sm: 2 },
                    borderRadius: '50px',
                    boxShadow: '0 8px 32px rgba(255,255,255,0.3), 0 4px 16px rgba(0,0,0,0.2)',
                    textTransform: 'none',
                    fontFamily: 'NanumSquareB',
                    letterSpacing: '0.5px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      bgcolor: '#f8f9fa',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(255,255,255,0.4), 0 6px 20px rgba(0,0,0,0.3)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    }
                  }}
                >
                  구글 계정으로 시작하기
                </Button>
              </Box>

              {/* 닫기 버튼 */}
              <Button
                onClick={() => setShowLoginDialog(false)}
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  '&:hover': {
                    color: 'white',
                    background: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                나중에 하기
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* 스낵바 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* <HighlightImageExportModal
          open={imageExportModalOpen}
          onClose={() => setImageExportModalOpen(false)}
          highlight={selectedHighlightForExport}
          book={book}
          authorProfile={highlightAuthorProfile}
        /> */}

        {/* 하이라이트 바텀시트 */}
        <Dialog
          open={highlightBottomSheetOpen}
          onClose={handleCloseHighlightBottomSheet}
          fullWidth
          PaperProps={{
            sx: {
              position: 'fixed',
              m: 0,
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxWidth: '600px',
              width: '100%',
              boxShadow: 8,
              pb: 2,
              zIndex: 1302,
              bgcolor: '#fff',
            }
          }}
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
          keepMounted
        >
          <DialogTitle sx={{ textAlign: 'center', fontWeight: 600, fontSize: '1.1rem', pb: 1 }}>문장 옵션</DialogTitle>
          <DialogContent sx={{ p: 0, width: '100%', boxSizing: 'border-box' }}>
            <Button
              fullWidth
              onClick={handleBottomSheetImageExport}
              startIcon={<PhotoCameraIcon />}
              sx={sheetButtonSx}
            >
              이미지로 내보내기
            </Button>
            <Button
              fullWidth
              onClick={handleBottomSheetShare}
              startIcon={<ShareIcon />}
              sx={sheetButtonSx}
            >
              공유하기
            </Button>
            {currentUser && (
              <Button
                fullWidth
                onClick={handleBottomSheetReply}
                startIcon={<EditNoteIcon />}
                sx={sheetButtonSx}
              >
                답글 작성
              </Button>
            )}
            {isHighlightOwner && (
              <>
                <Button
                  fullWidth
                  onClick={handleBottomSheetEdit}
                  startIcon={<EditIcon />}
                  sx={sheetButtonSx}
                >
                  수정
                </Button>
                <Button
                  fullWidth
                  onClick={handleBottomSheetDelete}
                  startIcon={<DeleteIcon />}
                  sx={{ px: 2, py: 2, fontSize: '1.1rem', fontWeight: 700, color: 'error.main', justifyContent: 'flex-start' }}
                >
                  삭제
                </Button>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 1 }}>
            <Button onClick={handleCloseHighlightBottomSheet} sx={{ color: 'text.secondary' }}>닫기</Button>
          </DialogActions>
        </Dialog>

        {/* 답글 작성 다이얼로그 */}
        <Dialog
          open={replyDialogOpen}
          onClose={() => {
            setReplyDialogOpen(false);
            setReplyText('');
            setReplyingToHighlightId(null);
            setReplyingToHighlight(null);
          }}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: '16px',
              zIndex: 1400, // 바텀시트 위에 표시
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1.1rem', pb: 1 }}>
            답글 작성
          </DialogTitle>
          <DialogContent>
            {/* 원문장 표시 */}
            {replyingToHighlight && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#666', mb: 1.5, fontSize: '0.875rem', fontWeight: 500 }}>
                  원문장
                </Typography>
                <Box sx={{ 
                  bgcolor: '#f5f5f5', 
                  p: 1.5, 
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <Typography sx={{ 
                    fontFamily: 'NanumSquareR, sans-serif',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    color: '#333'
                  }}>
                    {replyingToHighlight?.text || ''}
                  </Typography>
                </Box>
              </Box>
            )}
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={4}
              placeholder="답글을 입력하세요..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'NanumSquareR, sans-serif',
                  fontSize: '0.9375rem',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#999',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#333',
                  },
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                setReplyDialogOpen(false);
                setReplyText('');
                setReplyingToHighlightId(null);
                setReplyingToHighlight(null);
              }}
              sx={{ color: 'text.secondary' }}
            >
              취소
            </Button>
            <Button
              onClick={handleSaveReply}
              variant="contained"
              disabled={!replyText.trim() || savingReply}
              sx={{
                bgcolor: '#333',
                color: '#fff',
                '&:hover': {
                  bgcolor: '#555',
                },
                '&:disabled': {
                  bgcolor: '#ccc',
                },
              }}
            >
              {savingReply ? '저장 중...' : '작성하기'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 개선된 플로팅 액션 버튼 - 기록 추가하기 */}
        {isHighlightOwner && (
          <Button
            variant="contained"
            onClick={handleNavigateToCreateHighlight}
            sx={{
              position: 'fixed',
              bottom: 80,
              right: 'calc(50% - 300px + 16px)', // 600px 최대 width 기준으로 중앙 정렬
              bgcolor: '#FFF59D',
              color: '#333',
              zIndex: 1200,
              minWidth: 48,
              width: 48,
              minHeight: 48,
              height: 48,
              p: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(255, 245, 157, 0.4), 0 2px 8px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                bgcolor: '#FFEB99',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(255, 245, 157, 0.5), 0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              '&:active': {
                transform: 'translateY(0px)',
                transition: 'all 0.1s ease-out',
              },
              transition: 'all 0.2s ease-in-out',
              // 모바일에서는 화면 우측에 고정
              '@media (max-width: 600px)': {
                right: 16,
                minWidth: 44,
                width: 44,
                minHeight: 44,
                height: 44,
              },
            }}
          >
            <AddIcon sx={{ fontSize: '1.5rem' }} />
          </Button>
        )}
      </Box>
    </>
  );
}

export default BookDetail; 